/* eslint-disable no-unused-vars */
import { useState, useCallback, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ── DB → App mappers ────────────────────────────────────────────────────────
const toCoach       = r => ({ id: r.id, name: r.name, email: r.email, password: r.password, role: r.role, organizationId: r.organization_id });
const toTeam        = r => ({ id: r.id, name: r.name, color: r.color, emoji: r.emoji, teamPoints: r.team_points });
const toParticipant = r => ({ id: r.id, name: r.name, teamId: r.team_id, personalPoints: r.personal_points, bankBalance: r.bank_balance });
const toCategory    = r => ({ id: r.id, name: r.name, type: r.type, defaultPts: r.default_pts, active: r.active, emoji: r.emoji });
const toTx          = r => ({ id: r.id, type: r.type, participantId: r.participant_id, categoryId: r.category_id, teamId: r.team_id, amount: r.amount, coachId: r.coach_id, note: r.note || "", createdAt: r.created_at, corrected: r.corrected, correctionNote: r.correction_note, correctedBy: r.corrected_by });

// ── Config helper ────────────────────────────────────────────────────────────
const toConfig = r => ({ eventName: r.event_name, subtitle: r.subtitle, logoUrl: r.logo_url, accentColor: r.accent_color });
const DEFAULT_CONFIG = { eventName: "Mi Evento", subtitle: "Sistema de puntos", logoUrl: null, accentColor: "#C9A840" };

// ── Store ───────────────────────────────────────────────────────────────────
const useStore = (orgId) => {
  const [coaches,      setCoaches]      = useState([]);
  const [teams,        setTeams]        = useState([]);
  const [participants, setParticipants] = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [config,       setConfig]       = useState(DEFAULT_CONFIG);
  const [loading,      setLoading]      = useState(false);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      supabase.from("coaches").select("*").eq("organization_id", orgId).order("id"),
      supabase.from("teams").select("*").eq("organization_id", orgId).order("id"),
      supabase.from("participants").select("*").eq("organization_id", orgId).order("id"),
      supabase.from("categories").select("*").eq("organization_id", orgId).order("id"),
      supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
      supabase.from("config").select("*").eq("organization_id", orgId).single(),
    ]).then(([c, t, p, cat, tx, cfg]) => {
      setCoaches((c.data || []).map(toCoach));
      setTeams((t.data || []).map(toTeam));
      setParticipants((p.data || []).map(toParticipant));
      setCategories((cat.data || []).map(toCategory));
      setTransactions((tx.data || []).map(toTx));
      if (cfg.data) setConfig(toConfig(cfg.data));
      setLoading(false);
    });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const ch = supabase.channel(`org-rt-${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "teams", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("teams").select("*").eq("organization_id", orgId).order("id");
        if (data) setTeams(data.map(toTeam));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "participants", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("participants").select("*").eq("organization_id", orgId).order("id");
        if (data) setParticipants(data.map(toParticipant));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "transactions", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false });
        if (data) setTransactions(data.map(toTx));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "categories", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("categories").select("*").eq("organization_id", orgId).order("id");
        if (data) setCategories(data.map(toCategory));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "coaches", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("coaches").select("*").eq("organization_id", orgId).order("id");
        if (data) setCoaches(data.map(toCoach));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "config", filter: `organization_id=eq.${orgId}` }, async () => {
        const { data } = await supabase.from("config").select("*").eq("organization_id", orgId).single();
        if (data) setConfig(toConfig(data));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgId]);

  const refetchTx = useCallback(async () => { const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }); if (data) setTransactions(data.map(toTx)); }, [orgId]);

  const givePersonalPoints = useCallback(async (participantId, categoryId, amount, coachId, note = "") => {
    const p = participants.find(x => x.id === participantId); if (!p) return;
    setParticipants(prev => prev.map(x => x.id === participantId ? { ...x, personalPoints: x.personalPoints + amount } : x));
    await Promise.all([
      supabase.from("participants").update({ personal_points: p.personalPoints + amount }).eq("id", participantId),
      supabase.from("transactions").insert({ type: "personal_points", participant_id: participantId, category_id: categoryId, amount, coach_id: coachId, note, organization_id: orgId }),
    ]);
    const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }); if (data) setTransactions(data.map(toTx));
  }, [participants, orgId]);

  const giveTeamPoints = useCallback(async (teamId, categoryId, amount, coachId, note = "") => {
    const t = teams.find(x => x.id === teamId); if (!t) return;
    setTeams(prev => prev.map(x => x.id === teamId ? { ...x, teamPoints: x.teamPoints + amount } : x));
    await Promise.all([
      supabase.from("teams").update({ team_points: t.teamPoints + amount }).eq("id", teamId),
      supabase.from("transactions").insert({ type: "team_points", team_id: teamId, category_id: categoryId, amount, coach_id: coachId, note, organization_id: orgId }),
    ]);
    const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }); if (data) setTransactions(data.map(toTx));
  }, [teams, orgId]);

  const depositBank = useCallback(async (participantId, amount, coachId) => {
    const p = participants.find(x => x.id === participantId); if (!p) return;
    setParticipants(prev => prev.map(x => x.id === participantId ? { ...x, bankBalance: x.bankBalance + amount } : x));
    await Promise.all([
      supabase.from("participants").update({ bank_balance: p.bankBalance + amount }).eq("id", participantId),
      supabase.from("transactions").insert({ type: "bank_deposit", participant_id: participantId, amount, coach_id: coachId, note: "Depósito de billetes", organization_id: orgId }),
    ]);
    const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }); if (data) setTransactions(data.map(toTx));
  }, [participants, orgId]);

  const correctTransaction = useCallback(async (txId, newAmount, coachId, reason) => {
    const tx = transactions.find(x => x.id === txId); if (!tx) return;
    const diff = newAmount - tx.amount;
    const ops = [supabase.from("transactions").update({ amount: newAmount, corrected: true, correction_note: reason, corrected_by: coachId }).eq("id", txId)];
    if (tx.type === "personal_points" && tx.participantId) {
      const p = participants.find(x => x.id === tx.participantId);
      if (p) { setParticipants(prev => prev.map(x => x.id === tx.participantId ? { ...x, personalPoints: x.personalPoints + diff } : x)); ops.push(supabase.from("participants").update({ personal_points: p.personalPoints + diff }).eq("id", tx.participantId)); }
    } else if (tx.type === "team_points" && tx.teamId) {
      const t = teams.find(x => x.id === tx.teamId);
      if (t) { setTeams(prev => prev.map(x => x.id === tx.teamId ? { ...x, teamPoints: x.teamPoints + diff } : x)); ops.push(supabase.from("teams").update({ team_points: t.teamPoints + diff }).eq("id", tx.teamId)); }
    }
    await Promise.all(ops);
    const { data } = await supabase.from("transactions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }); if (data) setTransactions(data.map(toTx));
  }, [transactions, participants, teams, orgId]);

  const addParticipant    = useCallback(async (name, teamId) => { await supabase.from("participants").insert({ name: name.trim(), team_id: parseInt(teamId), personal_points: 0, bank_balance: 0, organization_id: orgId }); const { data } = await supabase.from("participants").select("*").eq("organization_id", orgId).order("id"); if (data) setParticipants(data.map(toParticipant)); }, [orgId]);
  const refetchTeams        = useCallback(async () => { const { data } = await supabase.from("teams").select("*").eq("organization_id", orgId).order("id"); if (data) setTeams(data.map(toTeam)); }, [orgId]);
  const refetchParticipants = useCallback(async () => { const { data } = await supabase.from("participants").select("*").eq("organization_id", orgId).order("id"); if (data) setParticipants(data.map(toParticipant)); }, [orgId]);
  const refetchCategories   = useCallback(async () => { const { data } = await supabase.from("categories").select("*").eq("organization_id", orgId).order("id"); if (data) setCategories(data.map(toCategory)); }, [orgId]);
  const refetchCoaches      = useCallback(async () => { const { data } = await supabase.from("coaches").select("*").eq("organization_id", orgId).order("id"); if (data) setCoaches(data.map(toCoach)); }, [orgId]);

  const editParticipant   = useCallback(async (id, name, teamId) => { await supabase.from("participants").update({ name: name.trim(), team_id: parseInt(teamId) }).eq("id", id); const { data } = await supabase.from("participants").select("*").eq("organization_id", orgId).order("id"); if (data) setParticipants(data.map(toParticipant)); }, [orgId]);
  const deleteParticipant = useCallback(async (id) => { await supabase.from("participants").delete().eq("id", id); const { data } = await supabase.from("participants").select("*").eq("organization_id", orgId).order("id"); if (data) setParticipants(data.map(toParticipant)); }, [orgId]);
  const addTeam           = useCallback(async (name, color, emoji) => { await supabase.from("teams").insert({ name: name.trim(), color, emoji, team_points: 0, organization_id: orgId }); const { data } = await supabase.from("teams").select("*").eq("organization_id", orgId).order("id"); if (data) setTeams(data.map(toTeam)); }, [orgId]);
  const editTeam          = useCallback(async (id, name, color, emoji) => { await supabase.from("teams").update({ name: name.trim(), color, emoji }).eq("id", id); const { data } = await supabase.from("teams").select("*").eq("organization_id", orgId).order("id"); if (data) setTeams(data.map(toTeam)); }, [orgId]);
  const deleteTeam        = useCallback(async (id) => { await supabase.from("teams").delete().eq("id", id); const { data } = await supabase.from("teams").select("*").eq("organization_id", orgId).order("id"); if (data) setTeams(data.map(toTeam)); }, [orgId]);
  const addCoach          = useCallback(async (name, email, password, role) => { await supabase.from("coaches").insert({ name: name.trim(), email: email.trim(), password, role, organization_id: orgId }); const { data } = await supabase.from("coaches").select("*").eq("organization_id", orgId).order("id"); if (data) setCoaches(data.map(toCoach)); }, [orgId]);
  const editCoach         = useCallback(async (id, name, email, password, role) => { await supabase.from("coaches").update({ name, email, password, role }).eq("id", id); const { data } = await supabase.from("coaches").select("*").eq("organization_id", orgId).order("id"); if (data) setCoaches(data.map(toCoach)); }, [orgId]);
  const deleteCoach       = useCallback(async (id) => { await supabase.from("coaches").delete().eq("id", id); const { data } = await supabase.from("coaches").select("*").eq("organization_id", orgId).order("id"); if (data) setCoaches(data.map(toCoach)); }, [orgId]);
  const addCategory       = useCallback(async (name, type, defaultPts, emoji) => { await supabase.from("categories").insert({ name: name.trim(), type, default_pts: parseInt(defaultPts), active: true, emoji, organization_id: orgId }); const { data } = await supabase.from("categories").select("*").eq("organization_id", orgId).order("id"); if (data) setCategories(data.map(toCategory)); }, [orgId]);
  const toggleCategory    = useCallback(async (id) => { const cat = categories.find(c => c.id === id); if (!cat) return; await supabase.from("categories").update({ active: !cat.active }).eq("id", id); const { data } = await supabase.from("categories").select("*").eq("organization_id", orgId).order("id"); if (data) setCategories(data.map(toCategory)); }, [categories, orgId]);
  const deleteCategory    = useCallback(async (id) => { await supabase.from("categories").delete().eq("id", id); const { data } = await supabase.from("categories").select("*").eq("organization_id", orgId).order("id"); if (data) setCategories(data.map(toCategory)); }, [orgId]);

  const updateConfig = useCallback(async (fields) => {
    setConfig(prev => ({ ...prev, ...fields }));
    await supabase.from("config").update({
      event_name:   fields.eventName   ?? undefined,
      subtitle:     fields.subtitle    ?? undefined,
      logo_url:     fields.logoUrl     !== undefined ? fields.logoUrl : undefined,
      accent_color: fields.accentColor ?? undefined,
    }).eq("organization_id", orgId);
  }, [orgId]);

  return { loading, config, coaches, teams, participants, categories, transactions, givePersonalPoints, giveTeamPoints, depositBank, correctTransaction, addParticipant, editParticipant, deleteParticipant, addTeam, editTeam, deleteTeam, addCoach, editCoach, deleteCoach, addCategory, toggleCategory, deleteCategory, updateConfig };
};

// ── Helpers ─────────────────────────────────────────────────────────────────
const getInitials  = name => name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";
const getTeamColor = (teams, teamId) => teams.find(t => t.id === teamId)?.color || "#4A6080";
const getTeamName  = (teams, teamId) => teams.find(t => t.id === teamId)?.name || "Sin equipo";
const formatTime   = d => new Date(d).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
const formatDate   = d => new Date(d).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
const COLORS = ["#E63946","#2A9D8F","#E9C46A","#F4A261","#6A4C93","#1982C4","#E76F51","#264653","#A8DADC","#43AA8B"];
const NAV = [
  { key: "dashboard",     icon: "⚡", label: "Dashboard" },
  { key: "rankings",      icon: "🏆", label: "Rankings" },
  { key: "participantes", icon: "👤", label: "Participantes" },
  { key: "equipos",       icon: "🛡️", label: "Equipos" },
  { key: "historial",     icon: "📋", label: "Historial" },
  { key: "admin",         icon: "⚙️", label: "Admin" },
];

// ── CSS ─────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  :root{
    --bg:#0B1520; --bg2:#0F1E30; --bg3:#142436; --sb:#091422;
    --border:#1A2E44; --accent:#C9A840; --accent2:#E07D3C; /* accent overridden dynamically */
    --text:#E8EDF5; --muted:#5A7A9A; --success:#22C55E; --danger:#EF4444;
    --radius:12px; --font-head:'DM Sans',sans-serif; --font-body:'DM Sans',sans-serif;
  }
  body{background:var(--bg);color:var(--text);font-family:var(--font-body);}
  input,select,textarea{color:var(--text);}

  /* ── Layout ── */
  .app{display:flex;min-height:100vh;}
  .sidebar{width:220px;flex-shrink:0;background:var(--sb);border-right:1px solid var(--border);display:flex;flex-direction:column;position:fixed;top:0;left:0;height:100vh;z-index:50;overflow-y:auto;}
  .main{flex:1;margin-left:220px;min-height:100vh;display:flex;flex-direction:column;}
  .content{flex:1;padding:28px 32px;overflow-y:auto;}

  /* ── Sidebar ── */
  .sb-logo{padding:18px 16px;border-bottom:1px solid var(--border);}
  .sb-logo-box{background:#fff;border-radius:8px;padding:8px 12px;display:block;}
  .sb-logo-box img{width:100%;display:block;}
  .sb-nav{flex:1;padding:10px 8px;display:flex;flex-direction:column;gap:2px;}
  .sb-btn{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;border:none;background:none;color:var(--muted);font-family:var(--font-body);font-size:13.5px;font-weight:500;cursor:pointer;text-align:left;width:100%;transition:all 0.15s;}
  .sb-btn:hover{background:var(--bg3);color:var(--text);}
  .sb-btn.active{background:#C9A84015;color:var(--accent);font-weight:600;border-left:2px solid var(--accent);padding-left:10px;}
  .sb-btn .sb-ic{font-size:15px;width:20px;text-align:center;}
  .sb-footer{padding:14px;border-top:1px solid var(--border);}
  .sb-name{font-size:13px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:2px;}
  .sb-role{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;}
  .sb-out{width:100%;background:transparent;border:1px solid var(--border);border-radius:7px;padding:7px;color:var(--muted);font-size:12px;cursor:pointer;font-family:var(--font-body);}
  .sb-out:hover{border-color:var(--danger);color:var(--danger);}

  /* ── Login ── */
  .login-wrap{min-height:100vh;display:grid;grid-template-columns:420px 1fr;}
  .login-left{background:var(--sb);border-right:1px solid var(--border);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 48px;gap:28px;}
  .ll-logo{background:#fff;border-radius:14px;padding:20px 32px;}
  .ll-logo img{width:200px;display:block;}
  .ll-text{text-align:center;}
  .ll-title{font-family:var(--font-head);font-size:20px;font-weight:800;color:var(--text);margin-bottom:6px;}
  .ll-sub{font-size:12px;color:var(--muted);letter-spacing:2px;text-transform:uppercase;}
  .login-right{display:flex;align-items:center;justify-content:center;padding:60px;}
  .login-card{width:100%;max-width:360px;}
  .login-title{font-family:var(--font-head);font-size:26px;font-weight:800;margin-bottom:4px;}
  .login-hint{font-size:13px;color:var(--muted);margin-bottom:32px;}
  .f-label{font-size:11px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin-bottom:7px;}
  .f-input{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:13px 15px;color:var(--text);font-family:var(--font-body);font-size:14px;outline:none;transition:border-color 0.2s;margin-bottom:14px;}
  .f-input:focus{border-color:var(--accent);}
  .btn-primary{width:100%;background:var(--accent);color:#0B1520;border:none;border-radius:9px;padding:14px;font-family:var(--font-head);font-size:15px;font-weight:700;cursor:pointer;}
  .btn-primary:hover{opacity:0.9;}
  .login-error{background:#EF444418;border:1px solid var(--danger);border-radius:8px;padding:10px 14px;color:var(--danger);font-size:13px;margin-bottom:14px;}

  /* ── Page header ── */
  .ph{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;padding-bottom:18px;border-bottom:1px solid var(--border);}
  .ph-left .ph-title{font-family:var(--font-head);font-size:22px;font-weight:800;}
  .ph-left .ph-sub{font-size:13px;color:var(--muted);margin-top:2px;}
  .ph-right{display:flex;gap:10px;align-items:center;}

  /* ── Stat tiles ── */
  .stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px;}
  .stat-tile{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);padding:18px 20px;position:relative;overflow:hidden;}
  .stat-tile::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--accent);}
  .st-val{font-family:var(--font-head);font-size:32px;font-weight:800;color:var(--accent);}
  .st-lbl{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:1.5px;margin-top:4px;}
  .st-delta{font-size:12px;color:var(--success);margin-top:6px;font-weight:600;}

  /* ── Dashboard 3-col ── */
  .dash3{display:grid;grid-template-columns:230px 1fr 280px;gap:18px;align-items:start;}

  /* ── Panel ── */
  .panel{background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;}
  .panel-head{padding:14px 18px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;}
  .panel-title{font-family:var(--font-head);font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:var(--muted);}
  .panel-body{padding:14px;}
  .panel-body-p0{padding:0;}

  /* ── Quick action buttons ── */
  .qab{display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;border-bottom:1px solid var(--border);padding:14px 18px;cursor:pointer;text-align:left;transition:background 0.15s;font-family:var(--font-body);}
  .qab:last-child{border-bottom:none;}
  .qab:hover{background:var(--bg3);}
  .qab-ic{font-size:20px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:8px;flex-shrink:0;}
  .qab-info .qab-title{font-size:13px;font-weight:600;color:var(--text);}
  .qab-info .qab-sub{font-size:11px;color:var(--muted);margin-top:1px;}
  .qab-arr{color:var(--muted);font-size:14px;margin-left:auto;}

  /* ── Team bar row ── */
  .trr{padding:11px 18px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px;}
  .trr:last-child{border-bottom:none;}
  .trr-pos{font-family:var(--font-head);font-size:13px;font-weight:800;width:22px;text-align:center;color:var(--muted);}
  .trr-pos.g{color:#FFD700;} .trr-pos.s{color:#C0C0C0;} .trr-pos.b{color:#CD7F32;}
  .trr-emoji{font-size:18px;}
  .trr-info{flex:1;min-width:0;}
  .trr-name{font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .trr-bar{height:4px;background:var(--border);border-radius:2px;margin-top:5px;overflow:hidden;}
  .trr-fill{height:4px;border-radius:2px;transition:width 0.5s;}
  .trr-pts{font-family:var(--font-head);font-size:14px;font-weight:800;flex-shrink:0;}

  /* ── Feed ── */
  .feed-item{display:flex;align-items:flex-start;gap:10px;padding:11px 18px;border-bottom:1px solid var(--border);}
  .feed-item:last-child{border-bottom:none;}
  .feed-ic{font-size:16px;margin-top:1px;flex-shrink:0;}
  .feed-body{flex:1;min-width:0;}
  .feed-desc{font-size:12px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .feed-meta{font-size:10px;color:var(--muted);margin-top:2px;}
  .feed-amt{font-family:var(--font-head);font-size:13px;font-weight:800;flex-shrink:0;}

  /* ── Table ── */
  .tbl-wrap{overflow-x:auto;}
  .tbl{width:100%;border-collapse:separate;border-spacing:0 4px;}
  .tbl th{text-align:left;font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:1.5px;padding:0 16px 8px;font-weight:600;font-family:var(--font-body);white-space:nowrap;}
  .tbl td{background:var(--bg2);padding:12px 16px;border-top:1px solid var(--border);border-bottom:1px solid var(--border);font-size:13px;vertical-align:middle;}
  .tbl td:first-child{border-left:1px solid var(--border);border-radius:9px 0 0 9px;}
  .tbl td:last-child{border-right:1px solid var(--border);border-radius:0 9px 9px 0;}
  .tbl tr:hover td{background:var(--bg3);}
  .tbl-name{font-weight:600;font-size:14px;}
  .tbl-sub{font-size:11px;color:var(--muted);margin-top:2px;}

  /* ── Search/filter bar ── */
  .bar{display:flex;gap:10px;align-items:center;margin-bottom:18px;flex-wrap:wrap;}
  .bar-search{background:var(--bg2);border:1px solid var(--border);border-radius:8px;padding:9px 14px;color:var(--text);font-family:var(--font-body);font-size:13px;outline:none;min-width:200px;}
  .bar-search:focus{border-color:var(--accent);}
  .bar-chip{background:var(--bg2);border:1px solid var(--border);border-radius:20px;padding:6px 14px;font-size:12px;font-weight:500;cursor:pointer;color:var(--muted);font-family:var(--font-body);white-space:nowrap;}
  .bar-chip.active{background:#C9A84015;border-color:var(--accent);color:var(--accent);}

  /* ── Buttons ── */
  .btn-add{background:var(--accent);color:#0B1520;border:none;border-radius:8px;padding:8px 18px;font-family:var(--font-head);font-weight:700;font-size:13px;cursor:pointer;}
  .btn-add:hover{opacity:0.9;}
  .btn-sm{background:var(--bg3);border:1px solid var(--border);border-radius:7px;padding:5px 10px;font-size:12px;cursor:pointer;color:var(--muted);font-family:var(--font-body);white-space:nowrap;}
  .btn-sm:hover{border-color:var(--accent);color:var(--accent);}
  .btn-sm.danger{border-color:var(--danger)!important;color:var(--danger)!important;}
  .btn-sm.accent{border-color:var(--accent);color:var(--accent);background:#C9A84010;}
  .btn-back{background:none;border:1px solid var(--border);border-radius:8px;padding:7px 14px;color:var(--muted);font-size:13px;cursor:pointer;font-family:var(--font-body);}
  .btn-back:hover{border-color:var(--accent);color:var(--accent);}

  /* ── Avatar ── */
  .av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;font-family:var(--font-head);flex-shrink:0;}
  .av-lg{width:52px;height:52px;font-size:18px;}

  /* ── Modal ── */
  .overlay{position:fixed;inset:0;background:rgba(0,0,0,0.72);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn 0.15s;}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  .modal{background:var(--bg2);border-radius:16px;border:1px solid var(--border);padding:26px;width:100%;max-width:480px;max-height:90vh;overflow-y:auto;animation:mIn 0.2s cubic-bezier(.17,.67,.35,1);}
  .modal-lg{max-width:640px;}
  @keyframes mIn{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}
  .modal-title{font-family:var(--font-head);font-size:18px;font-weight:800;margin-bottom:4px;}
  .modal-sub{font-size:13px;color:var(--muted);margin-bottom:20px;}
  .modal-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;}
  .modal-close{background:none;border:none;color:var(--muted);font-size:20px;cursor:pointer;padding:4px 8px;}
  .modal-close:hover{color:var(--text);}

  /* ── Amount ctrl ── */
  .amt-ctrl{display:flex;align-items:center;gap:14px;background:var(--bg3);border:1px solid var(--border);border-radius:10px;padding:6px;margin-bottom:14px;}
  .amt-btn{background:var(--bg2);border:1px solid var(--border);border-radius:8px;width:44px;height:44px;font-size:22px;cursor:pointer;color:var(--text);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .amt-val{flex:1;text-align:center;font-family:var(--font-head);font-size:34px;font-weight:800;color:var(--accent);}
  .preset-row{display:flex;gap:7px;margin-bottom:14px;flex-wrap:wrap;}
  .preset{background:var(--bg3);border:1px solid var(--border);border-radius:20px;padding:6px 13px;font-size:12px;font-weight:600;cursor:pointer;color:var(--muted);}
  .preset:hover{border-color:var(--accent);color:var(--accent);}

  /* ── Selector grid ── */
  .sel-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px;}
  .sel-card{background:var(--bg3);border:2px solid var(--border);border-radius:9px;padding:12px;text-align:center;cursor:pointer;transition:border-color 0.15s;}
  .sel-card:hover{border-color:var(--accent)60;}
  .sel-card.on{border-color:var(--accent);background:#C9A84010;}
  .sel-em{font-size:20px;display:block;margin-bottom:3px;}
  .sel-nm{font-size:12px;font-weight:600;}
  .sel-pt{font-size:11px;color:var(--muted);margin-top:1px;}

  /* ── Confirm/cancel ── */
  .btn-confirm{width:100%;background:var(--accent);color:#0B1520;border:none;border-radius:9px;padding:14px;font-family:var(--font-head);font-size:15px;font-weight:800;cursor:pointer;}
  .btn-confirm:hover{opacity:0.9;}
  .btn-confirm.red{background:var(--danger);color:#fff;}
  .btn-cancel{width:100%;background:transparent;color:var(--muted);border:1px solid var(--border);border-radius:9px;padding:11px;font-family:var(--font-head);font-size:13px;font-weight:600;cursor:pointer;margin-top:8px;}
  .note-input{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:10px 13px;color:var(--text);font-family:var(--font-body);font-size:13px;outline:none;resize:none;margin-bottom:13px;}
  .note-input:focus{border-color:var(--accent);}
  .search-in{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:10px 13px;color:var(--text);font-family:var(--font-body);font-size:13px;outline:none;margin-bottom:12px;}
  .search-in:focus{border-color:var(--accent);}
  .f-sel{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:11px 13px;color:var(--text);font-family:var(--font-body);font-size:13px;outline:none;appearance:none;margin-bottom:13px;}
  .f-sel:focus{border-color:var(--accent);}

  /* ── Misc ── */
  .divider{height:1px;background:var(--border);margin:14px 0;}
  .empty{text-align:center;padding:52px 24px;color:var(--muted);}
  .empty-ic{font-size:40px;margin-bottom:10px;}
  .badge{display:inline-block;background:var(--bg3);border:1px solid var(--border);border-radius:20px;padding:2px 8px;font-size:10px;font-weight:600;color:var(--muted);margin-left:6px;}
  .badge.admin{background:#C9A84015;border-color:var(--accent);color:var(--accent);}
  .section-label{font-size:10px;font-weight:700;color:var(--muted);letter-spacing:2px;text-transform:uppercase;margin-bottom:10px;}
  .cat-tog{background:var(--bg3);border:1px solid var(--border);border-radius:7px;padding:4px 10px;font-size:11px;cursor:pointer;color:var(--muted);font-family:var(--font-body);}
  .cat-tog.on{background:#22C55E15;border-color:var(--success);color:var(--success);}
  .tx-corrected{font-size:10px;color:var(--accent2);background:#E07D3C18;border-radius:4px;padding:2px 6px;display:inline-block;margin-top:2px;}
  .spinner{width:30px;height:30px;border:3px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto 14px;}
  @keyframes spin{to{transform:rotate(360deg)}}
  .toast{position:fixed;top:20px;right:24px;background:var(--success);color:#fff;padding:11px 20px;border-radius:50px;font-weight:700;font-size:13px;z-index:999;font-family:var(--font-head);animation:tIn 0.3s;box-shadow:0 8px 24px rgba(0,0,0,0.4);}
  .toast.error{background:var(--danger);}
  @keyframes tIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
  @keyframes riseUp{from{opacity:0;transform:translateY(80px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeInDown{from{opacity:0;transform:translateY(-24px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulseGlow{0%,100%{text-shadow:0 0 40px #FFD70080,0 0 10px #FFD70040}50%{text-shadow:0 0 80px #FFD700cc,0 0 30px #FFD70080}}
  .rank-tabs{display:flex;background:var(--bg3);border-radius:9px;padding:3px;margin-bottom:20px;width:fit-content;gap:2px;}
  .rank-tab{padding:8px 20px;border-radius:7px;font-size:13px;font-weight:700;font-family:var(--font-head);cursor:pointer;color:var(--muted);border:none;background:none;}
  .rank-tab.active{background:var(--bg2);color:var(--accent);}
  .rank-item{display:flex;align-items:center;gap:12px;background:var(--bg2);border:1px solid var(--border);border-radius:11px;padding:13px 16px;margin-bottom:7px;}
  .rank-pos{font-family:var(--font-head);font-size:17px;font-weight:800;color:var(--muted);width:26px;text-align:center;}
  .rank-pos.gold{color:#FFD700;} .rank-pos.silver{color:#C0C0C0;} .rank-pos.bronze{color:#CD7F32;}
  .rank-pts{font-family:var(--font-head);font-size:18px;font-weight:800;color:var(--accent);}
  .prog{height:4px;background:var(--border);border-radius:2px;margin-top:5px;overflow:hidden;}
  .prog-fill{height:4px;border-radius:2px;}
  .team-card-page{border-radius:var(--radius);padding:18px;margin-bottom:10px;border:1px solid transparent;}
  .color-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}

  /* ── Responsive ── */
  @media(max-width:900px){
    .sidebar{display:none;}
    .main{margin-left:0;}
    .dash3{grid-template-columns:1fr;}
    .stats-row{grid-template-columns:1fr 1fr;}
    .login-wrap{grid-template-columns:1fr;}
    .login-left{display:none;}
  }
`;

// ── Toast ───────────────────────────────────────────────────────────────────
const Toast = ({ msg, type }) => <div className={`toast ${type === "error" ? "error" : ""}`}>{msg}</div>;

// ── Login ───────────────────────────────────────────────────────────────────
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState(""); const [pass, setPass] = useState(""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email || !pass || busy) return;
    setBusy(true); setError("");
    const err = await onLogin(email, pass);
    if (err) { setError(err); setBusy(false); }
  };
  return (
    <div className="login-wrap">
      <div className="login-left">
        <div className="ll-text">
          <div className="ll-title" style={{ fontSize: 32 }}>Pointcamp</div>
          <div className="ll-sub">Sistema de puntos</div>
        </div>
      </div>
      <div className="login-right">
        <div className="login-card">
          <div className="login-title">Bienvenido</div>
          <div className="login-hint">Ingresa con tu cuenta de coach</div>
          {error && <div className="login-error">{error}</div>}
          <div className="f-label">Correo</div>
          <input className="f-input" type="email" placeholder="coach@ejemplo.com" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && submit()} autoFocus />
          <div className="f-label">Contraseña</div>
          <input className="f-input" type="password" placeholder="••••••••" value={pass} onChange={e => { setPass(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && submit()} />
          <button className="btn-primary" onClick={submit} style={{ opacity: busy ? 0.7 : 1 }}>{busy ? "Verificando..." : "Entrar →"}</button>
        </div>
      </div>
    </div>
  );
};

// ── Sidebar ─────────────────────────────────────────────────────────────────
const Sidebar = ({ coach, tab, setTab, onLogout, config }) => (
  <aside className="sidebar">
    <div className="sb-logo">
      {config?.logoUrl
        ? <div className="sb-logo-box"><img src={config.logoUrl} alt={config.eventName || "Evento"} /></div>
        : <div style={{ padding: "10px 4px", fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 15, color: "var(--accent)", letterSpacing: -0.5 }}>{config?.eventName || "Pointcamp"}</div>
      }
    </div>
    <nav className="sb-nav">
      {NAV.map(n => (
        <button key={n.key} id={`tour-${n.key}`} className={`sb-btn ${tab === n.key ? "active" : ""}`} onClick={() => setTab(n.key)}>
          <span className="sb-ic">{n.icon}</span>{n.label}
        </button>
      ))}
      {coach.role === "superadmin" && (
        <button className={`sb-btn ${tab === "clientes" ? "active" : ""}`} onClick={() => setTab("clientes")}>
          <span className="sb-ic">🏢</span>Clientes
        </button>
      )}
    </nav>
    <div className="sb-footer">
      <div className="sb-name">{coach.name}</div>
      <div className="sb-role">{coach.role}</div>
      <button className="sb-out" onClick={onLogout}>Cerrar sesión</button>
    </div>
  </aside>
);

// ── Tour ─────────────────────────────────────────────────────────────────────
const TOUR_STEPS = [
  { id: null,                title: "👋 Bienvenido a Pointcamp",      body: "Te damos un recorrido rápido para que conozcas lo esencial en menos de un minuto." },
  { id: "tour-equipos",      title: "🛡️ Añade tus equipos",           body: "Ve a Equipos en el menú, crea cada equipo con nombre, emoji y color. Aquí también ves el ranking en tiempo real." },
  { id: "tour-participantes",title: "👤 Añade los participantes",      body: "En Participantes registras a cada persona y la asignas a su equipo. Pueden acumular puntos individuales además de los del equipo." },
  { id: "tour-admin",        title: "⚙️ Configura categorías",         body: "En Admin → Categorías defines qué tipos de puntos puedes dar: disciplina, deporte, actitud, etc. Cada categoría tiene nombre y emoji." },
  { id: "tour-acciones",     title: "⭐ Suma puntos",                  body: "Desde aquí asignas puntos personales o por equipo en segundos. Elige la categoría, el equipo o participante y confirma." },
  { id: "tour-banco",        title: "🏦 Cashout de puntos físicos",    body: "Si repartes billetes físicos en el evento, usa Depósito Banco para registrarlos en el sistema. El coach descuenta los billetes y suma los puntos." },
  { id: "tour-pantalla",     title: "📺 Marcador en vivo",             body: "Abre el marcador en una pestaña nueva y ponlo en el proyector. Se actualiza solo, sin recargar." },
  { id: "tour-podio",        title: "🏆 Podio final",                  body: "Al terminar, usa el podio para revelar ganadores uno por uno con animación y confetti. El momento que todos esperan." },
];

const Tour = ({ orgId, onDone }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const seen = localStorage.getItem(`tour_v2_${orgId}`);
  const current = TOUR_STEPS[step];
  const isLast  = step === TOUR_STEPS.length - 1;

  useEffect(() => {
    if (seen) { onDone(); return; }
    if (current.id) {
      const el = document.getElementById(current.id);
      if (el) {
        setRect(null);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => setRect(el.getBoundingClientRect()), 450);
      }
    } else { setRect(null); }
  }, [step, current.id, seen, onDone]);

  const finish = () => { localStorage.setItem(`tour_v2_${orgId}`, "1"); onDone(); };
  const next   = () => isLast ? finish() : setStep(s => s + 1);

  const tipStyle = rect
    ? { top: Math.min(rect.bottom + 14, window.innerHeight - 220), left: Math.min(Math.max(rect.left, 8), window.innerWidth - 316) }
    : { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
      <div style={{ position: "absolute", inset: 0, background: rect ? "transparent" : "rgba(0,0,0,0.55)" }} onClick={finish} />
      {rect && (
        <div style={{ position: "fixed", top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, border: "2px solid var(--accent)", borderRadius: 12, boxShadow: "0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 5px rgba(201,168,64,0.3)", pointerEvents: "none", zIndex: 10000 }} />
      )}
      <div style={{ position: "fixed", zIndex: 10001, background: "#0F1E30", border: "1px solid #1A2E44", borderRadius: 16, padding: "24px 26px", width: 300, boxShadow: "0 24px 64px rgba(0,0,0,0.7)", ...tipStyle }}>
        <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Paso {step + 1} de {TOUR_STEPS.length}</div>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{current.title}</div>
        <div style={{ fontSize: 14, color: "#B0C4D8", lineHeight: 1.65, marginBottom: 20 }}>{current.body}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={finish} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 13, cursor: "pointer", padding: 0 }}>Saltar</button>
          <button onClick={next} style={{ background: "var(--accent)", color: "#0B1520", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{isLast ? "¡Listo! 🎉" : "Siguiente →"}</button>
        </div>
      </div>
    </div>
  );
};

// ── GivePointsModal ─────────────────────────────────────────────────────────
const PT_DENOMS = [1000, 500, 200, 100, 50];
const GivePointsModal = ({ type, participants, teams, categories, coach, onConfirm, onClose, preselectedId }) => {
  const [step, setStep] = useState(preselectedId ? 2 : 1);
  const [selectedId, setSelectedId] = useState(preselectedId || null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [amount, setAmount] = useState(100);
  const [counts, setCounts] = useState({ 1000: 0, 500: 0, 200: 0, 100: 0, 50: 0 });
  const [mode, setMode] = useState("add");
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");

  const cats = categories.filter(c => c.type === type && c.active);
  const items = type === "personal"
    ? participants.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : teams;
  const selObj = type === "personal" ? participants.find(p => p.id === selectedId) : teams.find(t => t.id === selectedId);
  const selCatObj = cats.find(c => c.id === selectedCat);
  const teamTotal = PT_DENOMS.reduce((s, d) => s + d * counts[d], 0);
  const setCount = (d, v) => setCounts(c => ({ ...c, [d]: Math.max(0, v) }));
  const resetCounts = () => setCounts({ 1000: 0, 500: 0, 200: 0, 100: 0, 50: 0 });
  const confirmTeam = () => { if (teamTotal > 0) { onConfirm(selectedId, selectedCat, mode === "remove" ? -teamTotal : teamTotal, note); onClose(); } };
  const confirm = () => { onConfirm(selectedId, selectedCat, mode === "remove" ? -amount : amount, note); onClose(); };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-hd">
          <div>
            <div className="modal-title">{type === "personal" ? "👤 Puntos Personales" : "🏆 Puntos por Equipo"}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
              {type === "team"
                ? `Paso ${step} de 2 · ${step === 1 ? "¿Qué equipo?" : "¿Cuántos puntos?"}`
                : `Paso ${step} de 3 · ${step === 1 ? "¿A quién?" : step === 2 ? "¿Por qué?" : "¿Cuántos?"}`
              }
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {step > 1 && <button className="btn-sm" onClick={() => setStep(s => s - 1)}>← Atrás</button>}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {step === 1 && (
          <>
            {type === "personal" && <input className="search-in" placeholder="🔍 Buscar participante..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />}
            <div style={{ maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 5 }}>
              {items.map(item => (
                <button key={item.id} onClick={() => { setSelectedId(item.id); setStep(2); }}
                  style={{ display: "flex", alignItems: "center", gap: 12, background: selectedId === item.id ? "#C9A84015" : "var(--bg3)", border: `1px solid ${selectedId === item.id ? "var(--accent)" : "var(--border)"}`, borderRadius: 9, padding: "11px 14px", cursor: "pointer", text: "left" }}>
                  {type === "personal" ? (
                    <>
                      <div className="av" style={{ background: getTeamColor(teams, item.teamId) + "30", color: getTeamColor(teams, item.teamId) }}>{getInitials(item.name)}</div>
                      <div style={{ flex: 1, textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{getTeamName(teams, item.teamId)} · {item.personalPoints} pts</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 22 }}>{item.emoji}</span>
                      <div style={{ flex: 1, textAlign: "left" }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{item.teamPoints} pts</div>
                      </div>
                      <div className="color-dot" style={{ background: item.color }} />
                    </>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && type === "team" && (
          <>
            <div style={{ background: "var(--bg3)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1.5 }}>{selObj?.emoji} {selObj?.name} · Puntos actuales</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: selObj?.color || "var(--accent)" }}>{selObj?.teamPoints}</div>
              </div>
              <button className="btn-sm" onClick={() => setStep(1)}>← Cambiar</button>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button onClick={() => { setMode("add"); resetCounts(); }} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `2px solid ${mode === "add" ? "var(--success)" : "var(--border)"}`, background: mode === "add" ? "#22C55E15" : "var(--bg3)", color: mode === "add" ? "var(--success)" : "var(--muted)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>＋ Sumar</button>
              <button onClick={() => { setMode("remove"); resetCounts(); }} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `2px solid ${mode === "remove" ? "var(--danger)" : "var(--border)"}`, background: mode === "remove" ? "#EF444415" : "var(--bg3)", color: mode === "remove" ? "var(--danger)" : "var(--muted)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>－ Quitar</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              {PT_DENOMS.map(d => (
                <div key={d} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ background: "var(--accent)20", border: "1px solid var(--accent)40", borderRadius: 6, padding: "4px 10px", fontSize: 13, fontWeight: 700, color: "var(--accent)", width: 60, textAlign: "center" }}>{d} pts</div>
                  <div style={{ color: "var(--muted)", fontSize: 12, flex: 1 }}>× {counts[d]} = <strong style={{ color: counts[d] > 0 ? "var(--text)" : "var(--muted)" }}>{d * counts[d]}</strong></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button className="amt-btn" style={{ width: 34, height: 34, fontSize: 18 }} onClick={() => setCount(d, counts[d] - 1)}>−</button>
                    <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18, width: 28, textAlign: "center" }}>{counts[d]}</div>
                    <button className="amt-btn" style={{ width: 34, height: 34, fontSize: 18 }} onClick={() => setCount(d, counts[d] + 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: teamTotal > 0 ? "#C9A84015" : "var(--bg3)", border: `1px solid ${teamTotal > 0 ? "var(--accent)" : "var(--border)"}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600 }}>Total a {mode === "add" ? "sumar" : "quitar"}</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: teamTotal > 0 ? (mode === "add" ? "var(--success)" : "var(--danger)") : "var(--muted)" }}>{mode === "remove" && teamTotal > 0 ? "-" : ""}{teamTotal}</div>
            </div>

            <button className={`btn-confirm${mode === "remove" ? " red" : ""}`} onClick={confirmTeam} style={{ opacity: teamTotal > 0 ? 1 : 0.4 }}>
              {mode === "add" ? "✓" : "✕"} {mode === "add" ? "Sumar" : "Quitar"} {teamTotal} pts a {selObj?.name}
            </button>
            <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          </>
        )}

        {step === 2 && type === "personal" && (
          <>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>Para: <strong style={{ color: "var(--text)" }}>{selObj?.name}</strong></div>
            {cats.length === 0
              ? <div className="empty"><div className="empty-ic">📋</div><div>No hay categorías activas</div></div>
              : <div className="sel-grid">
                {cats.map(cat => (
                  <div key={cat.id} className={`sel-card ${selectedCat === cat.id ? "on" : ""}`} onClick={() => { setSelectedCat(cat.id); setAmount(cat.defaultPts); setStep(3); }}>
                    <span className="sel-em">{cat.emoji}</span>
                    <div className="sel-nm">{cat.name}</div>
                    <div className="sel-pt">{cat.defaultPts} pts</div>
                  </div>
                ))}
              </div>
            }
          </>
        )}

        {step === 3 && type === "personal" && (
          <>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>
              <strong style={{ color: "var(--text)" }}>{selObj?.name}</strong> · {selCatObj?.emoji} {selCatObj?.name}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button onClick={() => setMode("add")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `2px solid ${mode === "add" ? "var(--success)" : "var(--border)"}`, background: mode === "add" ? "#22C55E15" : "var(--bg3)", color: mode === "add" ? "var(--success)" : "var(--muted)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                ＋ Sumar
              </button>
              <button onClick={() => setMode("remove")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `2px solid ${mode === "remove" ? "var(--danger)" : "var(--border)"}`, background: mode === "remove" ? "#EF444415" : "var(--bg3)", color: mode === "remove" ? "var(--danger)" : "var(--muted)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                － Quitar
              </button>
            </div>
            <div className="amt-ctrl" style={{ borderColor: mode === "remove" ? "var(--danger)" : "var(--border)" }}>
              <button className="amt-btn" onClick={() => setAmount(a => Math.max(50, a - 50))}>−</button>
              <div className="amt-val" style={{ color: mode === "remove" ? "var(--danger)" : "var(--accent)" }}>{mode === "remove" ? "-" : ""}${amount}</div>
              <button className="amt-btn" onClick={() => setAmount(a => a + 50)}>+</button>
            </div>
            <div className="preset-row">{[50, 100, 200, 500, 1000].map(v => <button key={v} className="preset" onClick={() => setAmount(v)}>${v}</button>)}</div>
            <textarea className="note-input" rows={2} placeholder="Nota opcional..." value={note} onChange={e => setNote(e.target.value)} />
            {mode === "add"
              ? <button className="btn-confirm" onClick={confirm}>✓ Dar ${amount}</button>
              : <button className="btn-confirm red" onClick={confirm}>✕ Quitar ${amount}</button>
            }
            <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          </>
        )}
      </div>
    </div>
  );
};

// ── BankModal ───────────────────────────────────────────────────────────────
const BILLS = [500, 200, 100, 50];
const BankModal = ({ participants, teams, onConfirm, onClose, preselectedId }) => {
  const [selectedId, setSelectedId] = useState(preselectedId || null);
  const [counts, setCounts] = useState({ 500: 0, 200: 0, 100: 0, 50: 0 });
  const [search, setSearch] = useState("");
  const [step, setStep] = useState(preselectedId ? 2 : 1);
  const filtered = participants.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const selected = participants.find(p => p.id === selectedId);
  const total = BILLS.reduce((sum, b) => sum + b * counts[b], 0);
  const set = (bill, val) => setCounts(c => ({ ...c, [bill]: Math.max(0, val) }));
  const reset = () => setCounts({ 500: 0, 200: 0, 100: 0, 50: 0 });
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-hd">
          <div className="modal-title">🏦 Depósito al Banco</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {step === 1 && (
          <>
            <input className="search-in" placeholder="🔍 Buscar participante..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
            <div style={{ maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 5 }}>
              {filtered.map(p => (
                <button key={p.id} onClick={() => { setSelectedId(p.id); reset(); setStep(2); }}
                  style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 9, padding: "11px 14px", cursor: "pointer" }}>
                  <div className="av" style={{ background: getTeamColor(teams, p.teamId) + "30", color: getTeamColor(teams, p.teamId) }}>{getInitials(p.name)}</div>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>Banco: <strong style={{ color: "var(--accent)" }}>${selected?.id === p.id ? selected.bankBalance : p.bankBalance}</strong></div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        {step === 2 && selected && (
          <>
            <div style={{ background: "var(--bg3)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1.5 }}>{selected.name} · Saldo actual</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: "var(--accent)" }}>${selected.bankBalance}</div>
              </div>
              <button className="btn-sm" onClick={() => setStep(1)}>← Cambiar</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              {BILLS.map(bill => (
                <div key={bill} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ background: "var(--accent)20", border: "1px solid var(--accent)40", borderRadius: 6, padding: "4px 10px", fontSize: 13, fontWeight: 700, color: "var(--accent)", width: 60, textAlign: "center" }}>${bill}</div>
                  <div style={{ color: "var(--muted)", fontSize: 12, flex: 1 }}>× {counts[bill]} = <strong style={{ color: counts[bill] > 0 ? "var(--text)" : "var(--muted)" }}>${bill * counts[bill]}</strong></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button className="amt-btn" style={{ width: 34, height: 34, fontSize: 18 }} onClick={() => set(bill, counts[bill] - 1)}>−</button>
                    <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18, width: 28, textAlign: "center" }}>{counts[bill]}</div>
                    <button className="amt-btn" style={{ width: 34, height: 34, fontSize: 18 }} onClick={() => set(bill, counts[bill] + 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: total > 0 ? "#C9A84015" : "var(--bg3)", border: `1px solid ${total > 0 ? "var(--accent)" : "var(--border)"}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600 }}>Total a depositar</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: total > 0 ? "var(--accent)" : "var(--muted)" }}>${total}</div>
            </div>

            <button className="btn-confirm" onClick={() => { if (total > 0) { onConfirm(selectedId, total); onClose(); } }} style={{ opacity: total > 0 ? 1 : 0.4 }}>
              🏦 Depositar ${total}
            </button>
            <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          </>
        )}
      </div>
    </div>
  );
};

// ── Dashboard ───────────────────────────────────────────────────────────────
const Dashboard = ({ store, coach, onAction }) => {
  const { participants, teams, transactions } = store;
  const today = transactions.filter(tx => new Date(tx.createdAt).toDateString() === new Date().toDateString());
  const sortedTeams = [...teams].sort((a, b) => b.teamPoints - a.teamPoints);
  const maxPts = sortedTeams[0]?.teamPoints || 1;
  const posClass = i => i === 0 ? "g" : i === 1 ? "s" : i === 2 ? "b" : "";
  const feedItems = transactions.slice(0, 12);
  const txIcon = tx => tx.type === "bank_deposit" ? "🏦" : tx.type === "personal_points" ? (store.categories.find(c => c.id === tx.categoryId)?.emoji || "⭐") : "🏆";
  const txDesc = tx => {
    const p = store.participants.find(x => x.id === tx.participantId);
    const t = store.teams.find(x => x.id === tx.teamId);
    const cat = store.categories.find(x => x.id === tx.categoryId);
    if (tx.type === "bank_deposit") return `Banco · ${p?.name || ""}`;
    if (tx.type === "personal_points") return `${cat?.name || "Pts"} · ${p?.name || ""}`;
    return `${cat?.name || "Pts"} · ${t?.name || ""}`;
  };

  return (
    <div className="content">
      <div className="ph">
        <div className="ph-left">
          <div className="ph-title">Dashboard</div>
          <div className="ph-sub">{store.config?.eventName || "Mi Evento"} · {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
        <div style={{ fontSize: 13, color: "var(--muted)" }}>Coach: <strong style={{ color: "var(--text)" }}>{coach.name}</strong></div>
      </div>

      <div className="stats-row">
        <div className="stat-tile"><div className="st-val">{participants.length}</div><div className="st-lbl">Participantes</div></div>
        <div className="stat-tile"><div className="st-val">{teams.length}</div><div className="st-lbl">Equipos</div></div>
        <div className="stat-tile"><div className="st-val">{today.length}</div><div className="st-lbl">Movimientos hoy</div></div>
        <div className="stat-tile"><div className="st-val">{transactions.length}</div><div className="st-lbl">Total movimientos</div></div>
      </div>

      <div className="dash3">
        {/* Acciones */}
        <div className="panel">
          <div className="panel-head"><span className="panel-title">Acciones rápidas</span></div>
          <div className="panel-body-p0" id="tour-acciones">
            <button className="qab" onClick={() => onAction("personal")}>
              <div className="qab-ic" style={{ background: "#C9A84020", color: "var(--accent)" }}>⭐</div>
              <div className="qab-info"><div className="qab-title">Puntos Personales</div><div className="qab-sub">Asignar a un participante</div></div>
              <span className="qab-arr">›</span>
            </button>
            <button className="qab" onClick={() => onAction("team")}>
              <div className="qab-ic" style={{ background: "#E07D3C20", color: "var(--accent2)" }}>🏆</div>
              <div className="qab-info"><div className="qab-title">Puntos por Equipo</div><div className="qab-sub">Asignar a un equipo</div></div>
              <span className="qab-arr">›</span>
            </button>
            <button id="tour-banco" className="qab" onClick={() => onAction("bank")}>
              <div className="qab-ic" style={{ background: "#22C55E20", color: "var(--success)" }}>🏦</div>
              <div className="qab-info"><div className="qab-title">Depósito Banco</div><div className="qab-sub">Canjear billetes</div></div>
              <span className="qab-arr">›</span>
            </button>
            <a id="tour-pantalla" href={`/?pantalla&org=${coach.organizationId}`} target="_blank" rel="noreferrer" className="qab" style={{ textDecoration: "none" }}>
              <div className="qab-ic" style={{ background: "#1982C420", color: "#1982C4" }}>📺</div>
              <div className="qab-info"><div className="qab-title" style={{ color: "var(--text)" }}>Pantalla</div><div className="qab-sub">Marcador para proyector</div></div>
              <span className="qab-arr">›</span>
            </a>
            <a id="tour-podio" href={`/?podio&org=${coach.organizationId}`} target="_blank" rel="noreferrer" className="qab" style={{ textDecoration: "none" }}>
              <div className="qab-ic" style={{ background: "#FFD70020", color: "#FFD700" }}>🏆</div>
              <div className="qab-info"><div className="qab-title" style={{ color: "var(--text)" }}>Podio</div><div className="qab-sub">Premiación final con confetti</div></div>
              <span className="qab-arr">›</span>
            </a>
            <a href={`/?subasta&org=${coach.organizationId}`} target="_blank" rel="noreferrer" className="qab" style={{ textDecoration: "none" }}>
              <div className="qab-ic" style={{ background: "#EF444420", color: "#EF4444" }}>🏷️</div>
              <div className="qab-info"><div className="qab-title" style={{ color: "var(--text)" }}>Subasta</div><div className="qab-sub">Descontar puntos en vivo</div></div>
              <span className="qab-arr">›</span>
            </a>
          </div>
        </div>

        {/* Equipos */}
        <div className="panel">
          <div className="panel-head"><span className="panel-title">Ranking de Equipos</span><span style={{ fontSize: 12, color: "var(--muted)" }}>{teams.length} equipos</span></div>
          <div className="panel-body-p0">
            {sortedTeams.length === 0
              ? <div className="empty"><div className="empty-ic">🛡️</div><div>Sin equipos registrados</div></div>
              : sortedTeams.map((team, i) => (
                <div key={team.id} className="trr" style={{ background: i === 0 ? team.color + "08" : "transparent" }}>
                  <span className={`trr-pos ${posClass(i)}`}>{i + 1}</span>
                  <span className="trr-emoji">{team.emoji}</span>
                  <div className="trr-info">
                    <div className="trr-name" style={{ color: i === 0 ? team.color : "var(--text)" }}>{team.name}</div>
                    <div className="trr-bar"><div className="trr-fill" style={{ width: `${(team.teamPoints / maxPts) * 100}%`, background: team.color }} /></div>
                  </div>
                  <span className="trr-pts" style={{ color: i === 0 ? team.color : "var(--accent)" }}>{team.teamPoints}</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Feed */}
        <div className="panel">
          <div className="panel-head"><span className="panel-title">Actividad reciente</span></div>
          <div className="panel-body-p0">
            {feedItems.length === 0
              ? <div className="empty"><div className="empty-ic">📭</div><div>Sin movimientos aún</div></div>
              : feedItems.map(tx => (
                <div key={tx.id} className="feed-item">
                  <span className="feed-ic">{txIcon(tx)}</span>
                  <div className="feed-body">
                    <div className="feed-desc">{txDesc(tx)}</div>
                    <div className="feed-meta">{store.coaches.find(c => c.id === tx.coachId)?.name} · {formatDate(tx.createdAt)}</div>
                  </div>
                  <span className="feed-amt" style={{ color: tx.amount < 0 ? "var(--danger)" : "var(--success)" }}>
                    {tx.amount >= 0 ? `+${tx.amount}` : tx.amount}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Rankings ─────────────────────────────────────────────────────────────────
const Rankings = ({ store }) => {
  const { participants, teams } = store; const [tab, setTab] = useState("teams");
  const sortedTeams = [...teams].sort((a, b) => b.teamPoints - a.teamPoints);
  const sortedP = [...participants].sort((a, b) => (b.personalPoints + b.bankBalance) - (a.personalPoints + a.bankBalance));
  const maxT = sortedTeams[0]?.teamPoints || 1;
  const maxP = (sortedP[0]?.personalPoints + sortedP[0]?.bankBalance) || 1;
  return (
    <div className="content">
      <div className="ph"><div className="ph-left"><div className="ph-title">Rankings</div><div className="ph-sub">Clasificación en tiempo real</div></div></div>
      <div className="rank-tabs">
        <button className={`rank-tab ${tab === "teams" ? "active" : ""}`} onClick={() => setTab("teams")}>🏆 Equipos</button>
        <button className={`rank-tab ${tab === "personal" ? "active" : ""}`} onClick={() => setTab("personal")}>👤 Individual</button>
      </div>
      {tab === "teams" && (sortedTeams.length === 0
        ? <div className="empty"><div className="empty-ic">🛡️</div><div>Sin equipos</div></div>
        : sortedTeams.map((team, i) => (
          <div key={team.id} className="rank-item" style={{ borderColor: i === 0 ? team.color + "60" : "var(--border)" }}>
            <div className={`rank-pos ${i === 0 ? "gold" : i === 1 ? "silver" : i === 2 ? "bronze" : ""}`}>{i + 1}</div>
            <span style={{ fontSize: 24 }}>{team.emoji}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: i === 0 ? team.color : "var(--text)" }}>{team.name}</div>
              <div className="prog"><div className="prog-fill" style={{ width: `${(team.teamPoints / maxT) * 100}%`, background: team.color }} /></div>
            </div>
            <div className="rank-pts" style={{ color: i === 0 ? team.color : "var(--accent)" }}>{team.teamPoints}</div>
          </div>
        ))
      )}
      {tab === "personal" && (sortedP.length === 0
        ? <div className="empty"><div className="empty-ic">👤</div><div>Sin participantes</div></div>
        : sortedP.map((p, i) => {
          const tc = getTeamColor(teams, p.teamId); const total = p.personalPoints + p.bankBalance;
          return (
            <div key={p.id} className="rank-item">
              <div className={`rank-pos ${i === 0 ? "gold" : i === 1 ? "silver" : i === 2 ? "bronze" : ""}`}>{i + 1}</div>
              <div className="av" style={{ background: tc + "30", color: tc }}>{getInitials(p.name)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>{getTeamName(teams, p.teamId)} · banco: {p.bankBalance}</div>
                <div className="prog"><div className="prog-fill" style={{ width: `${(total / maxP) * 100}%`, background: "var(--accent)" }} /></div>
              </div>
              <div className="rank-pts">{total}</div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ── Participantes ────────────────────────────────────────────────────────────
const Participantes = ({ store, coach, onGivePoints, onBank }) => {
  const { participants, teams, transactions } = store;
  const [search, setSearch] = useState(""); const [teamFilter, setTeamFilter] = useState(null);
  const [detail, setDetail] = useState(null); const [showForm, setShowForm] = useState(false); const [editP, setEditP] = useState(null);
  const [form, setForm] = useState({ name: "", teamId: teams[0]?.id || "" });

  const filtered = participants
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    .filter(p => !teamFilter || p.teamId === teamFilter)
    .sort((a, b) => (b.personalPoints + b.bankBalance) - (a.personalPoints + a.bankBalance));

  const openEdit = p => { setForm({ name: p.name, teamId: p.teamId }); setEditP(p); setShowForm(true); };
  const openAdd = () => { setForm({ name: "", teamId: teams[0]?.id || "" }); setEditP(null); setShowForm(true); };
  const save = () => { if (!form.name.trim()) return; if (editP) store.editParticipant(editP.id, form.name, form.teamId); else store.addParticipant(form.name, form.teamId); setShowForm(false); };

  if (detail) {
    const p = participants.find(x => x.id === detail); if (!p) { setDetail(null); return null; }
    const pTxs = transactions.filter(tx => tx.participantId === p.id);
    const tc = getTeamColor(teams, p.teamId);
    return (
      <div className="content">
        <div className="ph">
          <div className="ph-left">
            <button className="btn-back" onClick={() => setDetail(null)}>← Volver</button>
          </div>
          <div className="ph-right">
            <button className="btn-add" onClick={() => onGivePoints(p.id)}>⭐ Dar puntos</button>
            <button className="btn-sm accent" onClick={() => onBank(p.id)}>🏦 Banco</button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20, alignItems: "start" }}>
          <div className="panel">
            <div className="panel-body" style={{ textAlign: "center", padding: 28 }}>
              <div className="av av-lg" style={{ background: tc + "30", color: tc, margin: "0 auto 12px" }}>{getInitials(p.name)}</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800 }}>{p.name}</div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, marginBottom: 20 }}>{getTeamName(teams, p.teamId)}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div style={{ background: "var(--bg3)", borderRadius: 9, padding: 12 }}><div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--accent)" }}>{p.personalPoints}</div><div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>Pts</div></div>
                <div style={{ background: "var(--bg3)", borderRadius: 9, padding: 12 }}><div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--success)" }}>{p.bankBalance}</div><div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>Banco</div></div>
                <div style={{ background: "var(--bg3)", borderRadius: 9, padding: 12 }}><div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800 }}>{p.personalPoints + p.bankBalance}</div><div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>Total</div></div>
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-head"><span className="panel-title">Historial de {p.name.split(" ")[0]}</span></div>
            <div className="panel-body">
              {pTxs.length === 0 ? <div className="empty"><div className="empty-ic">📭</div><div>Sin movimientos</div></div>
                : <div className="tbl-wrap"><table className="tbl">
                  <thead><tr><th>Tipo</th><th>Categoría</th><th>Cantidad</th><th>Coach</th><th>Hora</th></tr></thead>
                  <tbody>
                    {pTxs.map(tx => {
                      const cat = store.categories.find(c => c.id === tx.categoryId);
                      const c = store.coaches.find(x => x.id === tx.coachId);
                      return (
                        <tr key={tx.id}>
                          <td>{tx.type === "bank_deposit" ? "🏦 Banco" : tx.type === "personal_points" ? "⭐ Personal" : "🏆 Equipo"}</td>
                          <td>{cat ? `${cat.emoji} ${cat.name}` : "—"}</td>
                          <td><strong style={{ color: tx.amount < 0 ? "var(--danger)" : "var(--success)" }}>{tx.amount >= 0 ? `+${tx.amount}` : tx.amount}</strong>{tx.corrected && <span className="tx-corrected" style={{ marginLeft: 6 }}>Corregido</span>}</td>
                          <td style={{ color: "var(--muted)" }}>{c?.name}</td>
                          <td style={{ color: "var(--muted)", fontSize: 12 }}>{formatTime(tx.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              }
            </div>
          </div>
        </div>
        {showForm && (
          <div className="overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
            <div className="modal">
              <div className="modal-hd"><div className="modal-title">{editP ? "Editar" : "Nuevo"} Participante</div><button className="modal-close" onClick={() => setShowForm(false)}>✕</button></div>
              <div className="f-label">Nombre completo</div>
              <input className="f-input" placeholder="Nombre Apellido" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              <div className="f-label">Equipo</div>
              {teams.length === 0 ? <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 14 }}>Primero crea equipos</div>
                : <select className="f-sel" value={form.teamId} onChange={e => setForm(f => ({ ...f, teamId: parseInt(e.target.value) }))}>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.name}</option>)}
                </select>}
              <button className="btn-confirm" onClick={save}>{editP ? "Guardar cambios" : "Agregar"}</button>
              <button className="btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="content">
      <div className="ph">
        <div className="ph-left"><div className="ph-title">Participantes</div><div className="ph-sub">{participants.length} registrados</div></div>
        {coach.role === "admin" && <div className="ph-right"><button className="btn-add" onClick={openAdd}>+ Agregar</button></div>}
      </div>
      <div className="bar">
        <input className="bar-search" placeholder="🔍 Buscar participante..." value={search} onChange={e => setSearch(e.target.value)} />
        <button className={`bar-chip ${!teamFilter ? "active" : ""}`} onClick={() => setTeamFilter(null)}>Todos</button>
        {teams.map(t => <button key={t.id} className={`bar-chip ${teamFilter === t.id ? "active" : ""}`} onClick={() => setTeamFilter(teamFilter === t.id ? null : t.id)}>{t.emoji} {t.name}</button>)}
      </div>
      {filtered.length === 0
        ? <div className="empty"><div className="empty-ic">👤</div><div>{participants.length === 0 ? "Agrega participantes con el botón de arriba" : "Sin resultados"}</div></div>
        : <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>#</th><th>Nombre</th><th>Equipo</th><th>Pts personales</th><th>Banco</th><th>Total</th><th>Acciones</th></tr></thead>
          <tbody>
            {filtered.map((p, i) => {
              const tc = getTeamColor(teams, p.teamId);
              return (
                <tr key={p.id} style={{ cursor: "pointer" }}>
                  <td style={{ color: "var(--muted)", fontSize: 12 }}>{i + 1}</td>
                  <td onClick={() => setDetail(p.id)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="av" style={{ background: tc + "30", color: tc }}>{getInitials(p.name)}</div>
                      <div className="tbl-name">{p.name}</div>
                    </div>
                  </td>
                  <td onClick={() => setDetail(p.id)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="color-dot" style={{ background: tc }} />
                      <span style={{ color: "var(--muted)", fontSize: 12 }}>{getTeamName(teams, p.teamId)}</span>
                    </div>
                  </td>
                  <td onClick={() => setDetail(p.id)} style={{ fontFamily: "var(--font-head)", fontWeight: 700, color: "var(--accent)" }}>{p.personalPoints}</td>
                  <td onClick={() => setDetail(p.id)} style={{ fontFamily: "var(--font-head)", fontWeight: 700, color: "var(--success)" }}>{p.bankBalance}</td>
                  <td onClick={() => setDetail(p.id)} style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 16 }}>{p.personalPoints + p.bankBalance}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-sm accent" onClick={() => onGivePoints(p.id)}>⭐</button>
                      <button className="btn-sm" onClick={() => onBank(p.id)}>🏦</button>
                      {coach.role === "admin" && <>
                        <button className="btn-sm" onClick={() => openEdit(p)}>✏️</button>
                        <button className="btn-sm danger" onClick={() => { if (window.confirm(`¿Eliminar a ${p.name}?`)) store.deleteParticipant(p.id); }}>✕</button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      }
      {showForm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">{editP ? "Editar" : "Nuevo"} Participante</div><button className="modal-close" onClick={() => setShowForm(false)}>✕</button></div>
            <div className="f-label">Nombre completo</div>
            <input className="f-input" placeholder="Nombre Apellido" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            <div className="f-label">Equipo</div>
            {teams.length === 0 ? <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 14 }}>Primero crea equipos</div>
              : <select className="f-sel" value={form.teamId} onChange={e => setForm(f => ({ ...f, teamId: parseInt(e.target.value) }))}>
                {teams.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.name}</option>)}
              </select>}
            <button className="btn-confirm" onClick={save}>{editP ? "Guardar cambios" : "Agregar"}</button>
            <button className="btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Equipos ──────────────────────────────────────────────────────────────────
const TEAM_PRESETS = [50, 100, 200, 500, 1000];

const Equipos = ({ store, coach }) => {
  const { teams, participants, categories } = store;
  const [showForm, setShowForm]   = useState(false);
  const [editT, setEditT]         = useState(null);
  const [form, setForm]           = useState({ name: "", color: COLORS[0], emoji: "⚡" });
  const [active, setActive]       = useState(null); // { teamId, mode: "add"|"remove" }
  const [selAmount, setSelAmount] = useState(100);
  const [customAmt, setCustomAmt] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [lastOp, setLastOp]       = useState(null);

  const sortedTeams = [...teams].sort((a, b) => b.teamPoints - a.teamPoints);
  const openEdit = t => { setForm({ name: t.name, color: t.color, emoji: t.emoji }); setEditT(t); setShowForm(true); };
  const openAdd  = () => { setForm({ name: "", color: COLORS[teams.length % COLORS.length], emoji: "⚡" }); setEditT(null); setShowForm(true); };
  const save     = () => { if (!form.name.trim()) return; if (editT) store.editTeam(editT.id, form.name, form.color, form.emoji); else store.addTeam(form.name, form.color, form.emoji); setShowForm(false); };

  const openQuick = (teamId, mode) => {
    if (active?.teamId === teamId && active?.mode === mode) { setActive(null); return; }
    setActive({ teamId, mode });
    setSelAmount(100);
    setCustomAmt("");
    setUseCustom(false);
  };

  const applyPoints = async (team) => {
    const pts = useCustom ? parseInt(customAmt, 10) : selAmount;
    if (!pts || pts <= 0) return;
    const finalAmt = active.mode === "remove" ? -pts : pts;
    const teamCat  = categories.find(c => c.type === "team" && c.active);
    await store.giveTeamPoints(team.id, teamCat?.id || null, finalAmt, coach.id);
    setLastOp({ name: team.name, pts: finalAmt });
    setActive(null);
  };

  return (
    <div className="content">
      <div className="ph">
        <div className="ph-left">
          <div className="ph-title">Equipos</div>
          <div className="ph-sub">{teams.length} equipos · {participants.length} participantes
            {lastOp && <span style={{ marginLeft: 12, color: lastOp.pts > 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>{lastOp.pts > 0 ? `+${lastOp.pts}` : lastOp.pts} → {lastOp.name}</span>}
          </div>
        </div>
        {coach.role === "admin" && <div className="ph-right"><button className="btn-add" onClick={openAdd}>+ Equipo</button></div>}
      </div>
      {sortedTeams.length === 0 && <div className="empty"><div className="empty-ic">🛡️</div><div>Crea los equipos desde el botón de arriba</div></div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
        {sortedTeams.map((team, i) => {
          const members   = participants.filter(p => p.teamId === team.id);
          const isOpen    = active?.teamId === team.id;
          const isAdd     = isOpen && active.mode === "add";
          const isRemove  = isOpen && active.mode === "remove";
          return (
            <div key={team.id} className="team-card-page" style={{ background: team.color + "10", borderColor: isOpen ? team.color + "90" : i === 0 ? team.color + "60" : team.color + "25" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>{team.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 800, color: team.color }}>{team.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{members.length} integrantes</div>
                </div>
                {i === 0 && teams.length > 1 && <span style={{ fontSize: 18 }}>👑</span>}
                {coach.role === "admin" && <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-sm" onClick={() => openEdit(team)}>✏️</button>
                  <button className="btn-sm danger" onClick={() => { if (window.confirm(`¿Eliminar ${team.name}?`)) store.deleteTeam(team.id); }}>✕</button>
                </div>}
              </div>

              <div style={{ fontFamily: "var(--font-head)", fontSize: 32, fontWeight: 800, color: team.color, marginBottom: 14 }}>
                {team.teamPoints} <span style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-body)", fontWeight: 400 }}>pts</span>
              </div>

              {/* Quick points controls */}
              <div style={{ display: "flex", gap: 8, marginBottom: members.length > 0 ? 12 : 0 }}>
                <button onClick={() => openQuick(team.id, "add")}
                  style={{ flex: 1, padding: "8px", borderRadius: 8, border: `2px solid ${isAdd ? "var(--success)" : "var(--border)"}`, background: isAdd ? "#22C55E18" : "var(--bg3)", color: isAdd ? "var(--success)" : "var(--muted)", fontWeight: 800, fontSize: 18, cursor: "pointer" }}>
                  ＋
                </button>
                <button onClick={() => openQuick(team.id, "remove")}
                  style={{ flex: 1, padding: "8px", borderRadius: 8, border: `2px solid ${isRemove ? "var(--danger)" : "var(--border)"}`, background: isRemove ? "#EF444418" : "var(--bg3)", color: isRemove ? "var(--danger)" : "var(--muted)", fontWeight: 800, fontSize: 18, cursor: "pointer" }}>
                  －
                </button>
              </div>

              {isOpen && (
                <div style={{ background: "var(--bg3)", border: `1px solid ${active.mode === "add" ? "#22C55E40" : "#EF444440"}`, borderRadius: 10, padding: "12px", marginBottom: members.length > 0 ? 12 : 0 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {TEAM_PRESETS.map(v => (
                      <button key={v} onClick={() => { setSelAmount(v); setUseCustom(false); }}
                        style={{ padding: "6px 12px", borderRadius: 7, border: `1.5px solid ${!useCustom && selAmount === v ? (active.mode === "add" ? "var(--success)" : "var(--danger)") : "var(--border)"}`, background: !useCustom && selAmount === v ? (active.mode === "add" ? "#22C55E18" : "#EF444418") : "var(--bg2)", color: !useCustom && selAmount === v ? (active.mode === "add" ? "var(--success)" : "var(--danger)") : "var(--muted)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                        {v}
                      </button>
                    ))}
                    <input
                      type="number" min="1" placeholder="otro"
                      value={customAmt}
                      onChange={e => { setCustomAmt(e.target.value); setUseCustom(true); }}
                      onFocus={() => setUseCustom(true)}
                      style={{ width: 70, padding: "6px 10px", borderRadius: 7, border: `1.5px solid ${useCustom ? (active.mode === "add" ? "var(--success)" : "var(--danger)") : "var(--border)"}`, background: "var(--bg2)", color: "var(--text)", fontSize: 13, outline: "none" }}
                    />
                  </div>
                  <button onClick={() => applyPoints(team)}
                    style={{ width: "100%", padding: "9px", borderRadius: 8, border: "none", background: active.mode === "add" ? "var(--success)" : "var(--danger)", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                    {active.mode === "add" ? "＋" : "－"} {useCustom ? (customAmt || "?") : selAmount} pts a {team.name}
                  </button>
                </div>
              )}

              {members.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {members.map(m => <span key={m.id} style={{ background: team.color + "20", border: `1px solid ${team.color}40`, borderRadius: 6, padding: "2px 9px", fontSize: 11, color: team.color, fontWeight: 600 }}>{m.name.split(" ")[0]}</span>)}
              </div>}
            </div>
          );
        })}
      </div>
      {showForm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">{editT ? "Editar Equipo" : "Nuevo Equipo"}</div><button className="modal-close" onClick={() => setShowForm(false)}>✕</button></div>
            <div className="f-label">Nombre del equipo</div>
            <input className="f-input" placeholder="Ej: Panteras" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            <div className="f-label">Emoji / ícono</div>
            <input className="f-input" placeholder="🦁" value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} />
            <div className="f-label">Color del equipo</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {COLORS.map(c => <div key={c} onClick={() => setForm(f => ({ ...f, color: c }))} style={{ width: 32, height: 32, borderRadius: "50%", background: c, cursor: "pointer", border: form.color === c ? "3px solid white" : "3px solid transparent", boxShadow: form.color === c ? `0 0 0 2px ${c}` : "none" }} />)}
            </div>
            <input type="color" className="f-input" style={{ height: 42, padding: 4, cursor: "pointer", marginBottom: 14 }} value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} />
            <button className="btn-confirm" onClick={save}>{editT ? "Guardar cambios" : "Crear equipo"}</button>
            <button className="btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Historial ────────────────────────────────────────────────────────────────
const Historial = ({ store, coach }) => {
  const { transactions } = store;
  const [filter, setFilter] = useState("all");
  const [editTx, setEditTx] = useState(null); const [newAmt, setNewAmt] = useState(0); const [reason, setReason] = useState("");
  const filters = [{ key: "all", label: "Todos" }, { key: "personal_points", label: "Personales" }, { key: "team_points", label: "Equipos" }, { key: "bank_deposit", label: "Banco" }];
  const shown = filter === "all" ? transactions : transactions.filter(tx => tx.type === filter);
  return (
    <div className="content">
      <div className="ph"><div className="ph-left"><div className="ph-title">Historial</div><div className="ph-sub">{transactions.length} movimientos totales</div></div></div>
      <div className="bar">
        {filters.map(f => <button key={f.key} className={`bar-chip ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)}>{f.label}</button>)}
      </div>
      {shown.length === 0
        ? <div className="empty"><div className="empty-ic">📭</div><div>Sin movimientos</div></div>
        : <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>Tipo</th><th>Participante / Equipo</th><th>Categoría</th><th>Cantidad</th><th>Coach</th><th>Hora</th>{coach.role === "admin" && <th>Acc.</th>}</tr></thead>
          <tbody>
            {shown.map(tx => {
              const p = store.participants.find(x => x.id === tx.participantId);
              const t = store.teams.find(x => x.id === tx.teamId);
              const cat = store.categories.find(x => x.id === tx.categoryId);
              const c = store.coaches.find(x => x.id === tx.coachId);
              const icon = tx.type === "bank_deposit" ? "🏦" : tx.type === "personal_points" ? "⭐" : "🏆";
              const label = tx.type === "bank_deposit" ? "Banco" : tx.type === "personal_points" ? "Personal" : "Equipo";
              return (
                <tr key={tx.id}>
                  <td><span style={{ fontSize: 16 }}>{icon}</span> <span style={{ fontSize: 12, color: "var(--muted)" }}>{label}</span></td>
                  <td className="tbl-name">{p?.name || t?.name || "—"}</td>
                  <td style={{ color: "var(--muted)", fontSize: 12 }}>{cat ? `${cat.emoji} ${cat.name}` : "—"}</td>
                  <td>
                    <strong style={{ fontFamily: "var(--font-head)", color: tx.amount < 0 ? "var(--danger)" : "var(--success)" }}>{tx.amount >= 0 ? `+${tx.amount}` : tx.amount}</strong>
                    {tx.corrected && <span className="tx-corrected" style={{ marginLeft: 6 }}>Editado</span>}
                  </td>
                  <td style={{ color: "var(--muted)", fontSize: 12 }}>{c?.name}</td>
                  <td style={{ color: "var(--muted)", fontSize: 11 }}>{formatTime(tx.createdAt)}</td>
                  {coach.role === "admin" && <td><button className="btn-sm" onClick={() => { setEditTx(tx); setNewAmt(tx.amount); setReason(""); }}>✏️</button></td>}
                </tr>
              );
            })}
          </tbody>
        </table></div>
      }
      {editTx && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setEditTx(null)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">✏️ Corregir movimiento</div><button className="modal-close" onClick={() => setEditTx(null)}>✕</button></div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>Solo admins pueden corregir puntos</div>
            <div className="f-label">Nuevo valor</div>
            <div className="amt-ctrl"><button className="amt-btn" onClick={() => setNewAmt(a => a - 1)}>−</button><div className="amt-val">{newAmt}</div><button className="amt-btn" onClick={() => setNewAmt(a => a + 1)}>+</button></div>
            <div className="f-label">Motivo</div>
            <input className="f-input" placeholder="¿Por qué se corrige?" value={reason} onChange={e => setReason(e.target.value)} />
            <button className="btn-confirm" onClick={() => { store.correctTransaction(editTx.id, parseInt(newAmt), coach.id, reason); setEditTx(null); }}>Guardar corrección</button>
            <button className="btn-cancel" onClick={() => setEditTx(null)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Admin ────────────────────────────────────────────────────────────────────
const Admin = ({ store, coach }) => {
  const { categories, coaches, config } = store;
  const [tab, setTab] = useState("evento");
  const [showCatForm, setShowCatForm] = useState(false); const [showCoachForm, setShowCoachForm] = useState(false); const [editCoach, setEditCoach] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", type: "personal", defaultPts: "5", emoji: "⭐" });
  const [coachForm, setCoachForm] = useState({ name: "", email: "", password: "", role: "coach" });
  const [cfgForm, setCfgForm] = useState({ eventName: config?.eventName || "", subtitle: config?.subtitle || "", logoUrl: config?.logoUrl || "", accentColor: config?.accentColor || "#C9A840" });
  const [cfgSaved, setCfgSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const filename = `org-${coach.organizationId}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("logos").upload(filename, file, { upsert: true });
    if (!error) {
      const { data } = supabase.storage.from("logos").getPublicUrl(filename);
      setCfgForm(f => ({ ...f, logoUrl: data.publicUrl }));
    }
    setUploading(false);
  };

  useEffect(() => {
    if (config) setCfgForm({ eventName: config.eventName, subtitle: config.subtitle, logoUrl: config.logoUrl || "", accentColor: config.accentColor });
  }, [config]);

  if (coach.role !== "admin" && coach.role !== "superadmin") return <div className="content"><div className="empty"><div className="empty-ic">🔒</div><div>Solo admins pueden ver esta sección</div></div></div>;
  const saveCat = () => { if (!catForm.name.trim()) return; store.addCategory(catForm.name, catForm.type, catForm.defaultPts, catForm.emoji); setCatForm({ name: "", type: "personal", defaultPts: "5", emoji: "⭐" }); setShowCatForm(false); };
  const openAddCoach = () => { setCoachForm({ name: "", email: "", password: "", role: "coach" }); setEditCoach(null); setShowCoachForm(true); };
  const openEditCoach = c => { setCoachForm({ name: c.name, email: c.email, password: c.password, role: c.role }); setEditCoach(c); setShowCoachForm(true); };
  const saveCoach = () => { if (!coachForm.name.trim() || !coachForm.email.trim()) return; if (editCoach) store.editCoach(editCoach.id, coachForm.name, coachForm.email, coachForm.password, coachForm.role); else store.addCoach(coachForm.name, coachForm.email, coachForm.password, coachForm.role); setShowCoachForm(false); };
  const saveCfg = async () => { await store.updateConfig({ eventName: cfgForm.eventName, subtitle: cfgForm.subtitle, logoUrl: cfgForm.logoUrl || null, accentColor: cfgForm.accentColor }); setCfgSaved(true); setTimeout(() => setCfgSaved(false), 2000); };
  const personal = categories.filter(c => c.type === "personal"); const team = categories.filter(c => c.type === "team");

  return (
    <div className="content">
      <div className="ph"><div className="ph-left"><div className="ph-title">Admin</div><div className="ph-sub">Configuración del campamento</div></div></div>
      <div className="rank-tabs">
        <button className={`rank-tab ${tab === "evento" ? "active" : ""}`} onClick={() => setTab("evento")}>🎨 Evento</button>
        <button className={`rank-tab ${tab === "cats" ? "active" : ""}`} onClick={() => setTab("cats")}>📋 Categorías</button>
        <button className={`rank-tab ${tab === "coaches" ? "active" : ""}`} onClick={() => setTab("coaches")}>👥 Coaches</button>
      </div>

      {tab === "evento" && (
        <div style={{ maxWidth: 480 }}>
          <div className="f-label">Nombre del evento</div>
          <input className="f-input" placeholder="Ej: Summer Camp 2026" value={cfgForm.eventName} onChange={e => setCfgForm(f => ({ ...f, eventName: e.target.value }))} />
          <div className="f-label">Subtítulo</div>
          <input className="f-input" placeholder="Ej: Sistema de puntos" value={cfgForm.subtitle} onChange={e => setCfgForm(f => ({ ...f, subtitle: e.target.value }))} />
          <div className="f-label">Logo</div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            {cfgForm.logoUrl && <img src={cfgForm.logoUrl} alt="logo" style={{ height: 56, borderRadius: 8, background: "var(--bg3)", padding: 8, border: "1px solid var(--border)" }} onError={e => e.target.style.display = "none"} />}
            <label style={{ cursor: "pointer" }}>
              <div className="btn-sm accent" style={{ pointerEvents: "none" }}>{uploading ? "Subiendo..." : cfgForm.logoUrl ? "Cambiar logo" : "Subir logo"}</div>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => uploadLogo(e.target.files[0])} disabled={uploading} />
            </label>
            {cfgForm.logoUrl && <button className="btn-sm danger" onClick={() => setCfgForm(f => ({ ...f, logoUrl: "" }))}>Quitar</button>}
          </div>
          <div className="f-label">Color principal</div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
            <input type="color" value={cfgForm.accentColor} onChange={e => setCfgForm(f => ({ ...f, accentColor: e.target.value }))} style={{ width: 48, height: 48, border: "none", borderRadius: 8, cursor: "pointer", background: "transparent" }} />
            <input className="f-input" style={{ marginBottom: 0, flex: 1 }} value={cfgForm.accentColor} onChange={e => setCfgForm(f => ({ ...f, accentColor: e.target.value }))} placeholder="#C9A840" />
            <div style={{ width: 48, height: 48, borderRadius: 8, background: cfgForm.accentColor, flexShrink: 0 }} />
          </div>
          <button className="btn-confirm" onClick={saveCfg}>{cfgSaved ? "✓ Guardado" : "Guardar cambios"}</button>
        </div>
      )}

      {tab === "cats" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div className="section-label" style={{ margin: 0 }}>Categorías de puntos</div>
            <button className="btn-add" onClick={() => setShowCatForm(true)}>+ Nueva categoría</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div className="section-label" style={{ color: "var(--accent)", marginBottom: 12 }}>⭐ Personales</div>
              {personal.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "11px 14px", marginBottom: 7 }}>
                  <span style={{ fontSize: 18 }}>{c.emoji}</span>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.defaultPts} pts por defecto</div></div>
                  <button className={`cat-tog ${c.active ? "on" : ""}`} onClick={() => store.toggleCategory(c.id)}>{c.active ? "Activa" : "Inactiva"}</button>
                  <button className="btn-sm danger" onClick={() => store.deleteCategory(c.id)}>✕</button>
                </div>
              ))}
              {personal.length === 0 && <div style={{ color: "var(--muted)", fontSize: 13, padding: 12 }}>Sin categorías personales</div>}
            </div>
            <div>
              <div className="section-label" style={{ color: "var(--accent2)", marginBottom: 12 }}>🏆 Equipos</div>
              {team.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "11px 14px", marginBottom: 7 }}>
                  <span style={{ fontSize: 18 }}>{c.emoji}</span>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.defaultPts} pts por defecto</div></div>
                  <button className={`cat-tog ${c.active ? "on" : ""}`} onClick={() => store.toggleCategory(c.id)}>{c.active ? "Activa" : "Inactiva"}</button>
                  <button className="btn-sm danger" onClick={() => store.deleteCategory(c.id)}>✕</button>
                </div>
              ))}
              {team.length === 0 && <div style={{ color: "var(--muted)", fontSize: 13, padding: 12 }}>Sin categorías de equipo</div>}
            </div>
          </div>
        </>
      )}

      {tab === "coaches" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div className="section-label" style={{ margin: 0 }}>Coaches registrados</div>
            <button className="btn-add" onClick={openAddCoach}>+ Coach</button>
          </div>
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Coach</th><th>Correo</th><th>Rol</th><th>Acciones</th></tr></thead>
            <tbody>
              {coaches.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="av" style={{ background: c.role === "admin" ? "#C9A84030" : "var(--bg3)", color: c.role === "admin" ? "var(--accent)" : "var(--muted)" }}>{getInitials(c.name)}</div>
                      <strong>{c.name}</strong>
                    </div>
                  </td>
                  <td style={{ color: "var(--muted)", fontSize: 12 }}>{c.email}</td>
                  <td><span className={`badge ${c.role === "admin" ? "admin" : ""}`}>{c.role}</span></td>
                  <td><div style={{ display: "flex", gap: 6 }}>
                    <button className="btn-sm" onClick={() => openEditCoach(c)}>✏️ Editar</button>
                    {c.id !== coach.id && <button className="btn-sm danger" onClick={() => { if (window.confirm(`¿Eliminar a ${c.name}?`)) store.deleteCoach(c.id); }}>✕</button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}

      {showCatForm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowCatForm(false)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">Nueva Categoría</div><button className="modal-close" onClick={() => setShowCatForm(false)}>✕</button></div>
            <div className="f-label">Nombre</div>
            <input className="f-input" placeholder="Ej: Participación en misa" value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            <div className="f-label">Tipo</div>
            <select className="f-sel" value={catForm.type} onChange={e => setCatForm(f => ({ ...f, type: e.target.value }))}><option value="personal">Personal</option><option value="team">Equipo</option></select>
            <div className="f-label">Puntos por defecto</div>
            <input className="f-input" type="number" placeholder="5" value={catForm.defaultPts} onChange={e => setCatForm(f => ({ ...f, defaultPts: e.target.value }))} />
            <div className="f-label">Emoji</div>
            <input className="f-input" placeholder="⭐" value={catForm.emoji} onChange={e => setCatForm(f => ({ ...f, emoji: e.target.value }))} />
            <button className="btn-confirm" onClick={saveCat}>Crear categoría</button>
            <button className="btn-cancel" onClick={() => setShowCatForm(false)}>Cancelar</button>
          </div>
        </div>
      )}
      {showCoachForm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowCoachForm(false)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">{editCoach ? "Editar Coach" : "Nuevo Coach"}</div><button className="modal-close" onClick={() => setShowCoachForm(false)}>✕</button></div>
            <div className="f-label">Nombre</div>
            <input className="f-input" placeholder="Nombre del coach" value={coachForm.name} onChange={e => setCoachForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            <div className="f-label">Correo</div>
            <input className="f-input" type="email" placeholder="coach@ltc.mx" value={coachForm.email} onChange={e => setCoachForm(f => ({ ...f, email: e.target.value }))} />
            <div className="f-label">Contraseña</div>
            <input className="f-input" placeholder="Contraseña" value={coachForm.password} onChange={e => setCoachForm(f => ({ ...f, password: e.target.value }))} />
            <div className="f-label">Rol</div>
            <select className="f-sel" value={coachForm.role} onChange={e => setCoachForm(f => ({ ...f, role: e.target.value }))}><option value="coach">Coach</option><option value="admin">Admin</option></select>
            <button className="btn-confirm" onClick={saveCoach}>{editCoach ? "Guardar cambios" : "Agregar coach"}</button>
            <button className="btn-cancel" onClick={() => setShowCoachForm(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Ver (ranking público, solo lectura) ──────────────────────────────────────
const Ver = ({ store }) => {
  const { participants, teams } = store;
  const total  = p => p.personalPoints + p.bankBalance;
  const sorted = [...participants].sort((a, b) => total(b) - total(a));
  const medals = ["🥇", "🥈", "🥉"];
  const maxPts = total(sorted[0]) || 1;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "var(--font-body)" }}>
      <style>{`
        .ver-header{background:var(--bg2);border-bottom:1px solid var(--border);padding:20px 20px 16px;text-align:center;}
        .ver-logo{height:40px;filter:brightness(0) invert(1);margin-bottom:8px;}
        .ver-sub{font-size:11px;color:var(--muted);letter-spacing:3px;text-transform:uppercase;}
        .ver-list{padding:12px 14px;display:flex;flex-direction:column;gap:7px;max-width:520px;margin:0 auto;}
        .ver-row{background:var(--bg2);border:1px solid var(--border);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:10px;}
        .ver-row.top1{border-color:#FFD70060;background:#FFD70008;}
        .ver-row.top2{border-color:#C0C0C040;}
        .ver-row.top3{border-color:#CD7F3240;}
        .ver-rank{font-size:18px;width:32px;text-align:center;flex-shrink:0;}
        .ver-avatar{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;flex-shrink:0;}
        .ver-info{flex:1;min-width:0;}
        .ver-name{font-size:14px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ver-team{font-size:11px;color:var(--muted);}
        .ver-bar{height:3px;background:var(--border);border-radius:2px;margin-top:5px;}
        .ver-fill{height:3px;border-radius:2px;background:var(--accent);transition:width .5s;}
        .ver-pts{font-size:20px;font-weight:800;color:var(--accent);flex-shrink:0;}
        .ver-row.top1 .ver-pts{color:#FFD700;}
      `}</style>

      <div className="ver-header">
        <img src={store.config?.logoUrl || "/logo.png"} alt={store.config?.eventName || "Evento"} className="ver-logo" />
        <div className="ver-sub">Ranking individual · en vivo</div>
      </div>

      <div className="ver-list">
        {sorted.map((p, i) => {
          const color   = getTeamColor(teams, p.teamId);
          const cls     = i === 0 ? "top1" : i === 1 ? "top2" : i === 2 ? "top3" : "";
          const pts     = total(p);
          return (
            <div key={p.id} className={`ver-row ${cls}`}>
              <span className="ver-rank">{medals[i] || i + 1}</span>
              <div className="ver-avatar" style={{ background: color + "30", color }}>{getInitials(p.name)}</div>
              <div className="ver-info">
                <div className="ver-name">{p.name}</div>
                <div className="ver-team">{getTeamName(teams, p.teamId)}</div>
                <div className="ver-bar"><div className="ver-fill" style={{ width: `${(pts / maxPts) * 100}%` }} /></div>
              </div>
              <span className="ver-pts">{pts.toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Subasta ──────────────────────────────────────────────────────────────────
const Subasta = ({ store }) => {
  const { participants, teams } = store;
  const [selected, setSelected] = useState(null);
  const [amount, setAmount]     = useState("");
  const [saving, setSaving]     = useState(false);
  const [lastOp, setLastOp]     = useState(null);
  const inputRef = useRef(null);

  const total  = p => p.personalPoints + p.bankBalance;
  const sorted = [...participants].sort((a, b) => total(b) - total(a));

  const select = (id) => {
    setSelected(id);
    setAmount("");
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const confirm = async () => {
    const pts = parseInt(amount, 10);
    if (!pts || pts <= 0) return;
    setSaving(true);
    const p = participants.find(x => x.id === selected);
    const newVal = p.personalPoints - pts;
    await supabase.from("participants").update({ personal_points: newVal }).eq("id", selected);
    setLastOp({ name: p.name.split(" ")[0], pts });
    setSaving(false);
    setSelected(null);
    setAmount("");
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "var(--font-body)" }}>
      <style>{`
        .sub-header{background:var(--bg2);border-bottom:1px solid var(--border);padding:18px 20px;display:flex;align-items:center;gap:12px;}
        .sub-title{font-size:20px;font-weight:800;color:var(--accent);letter-spacing:-.5px;}
        .sub-toast{background:#22C55E18;border:1px solid #22C55E40;border-radius:8px;color:#22C55E;font-size:13px;padding:6px 14px;margin-left:auto;}
        .sub-list{padding:12px 16px;display:flex;flex-direction:column;gap:8px;max-width:600px;margin:0 auto;}
        .sub-row{background:var(--bg2);border:1px solid var(--border);border-radius:12px;padding:14px 16px;display:flex;align-items:center;gap:12px;cursor:pointer;transition:border-color .15s;}
        .sub-row.active{border-color:var(--accent);background:var(--bg3);}
        .sub-row:hover{border-color:var(--muted);}
        .sub-rank{font-size:12px;font-weight:700;color:var(--muted);width:22px;text-align:center;}
        .sub-avatar{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;flex-shrink:0;}
        .sub-name{flex:1;font-size:15px;font-weight:600;color:var(--text);}
        .sub-pts{font-size:15px;font-weight:700;color:var(--accent);}
        .sub-btn{background:var(--danger);color:#fff;border:none;border-radius:8px;padding:7px 14px;font-size:15px;font-weight:700;cursor:pointer;flex-shrink:0;}
        .sub-expand{padding:12px 0 4px;display:flex;gap:8px;align-items:center;}
        .sub-input{flex:1;background:var(--bg);border:1px solid var(--accent);border-radius:8px;padding:8px 12px;font-size:16px;color:var(--text);outline:none;}
        .sub-confirm{background:var(--danger);color:#fff;border:none;border-radius:8px;padding:8px 16px;font-size:15px;font-weight:700;cursor:pointer;}
        .sub-cancel{background:transparent;color:var(--muted);border:1px solid var(--border);border-radius:8px;padding:8px 14px;font-size:14px;cursor:pointer;}
      `}</style>

      <div className="sub-header">
        <span style={{ fontSize: 24 }}>🏷️</span>
        <span className="sub-title">Subasta de Premios</span>
        {lastOp && <span className="sub-toast">−{lastOp.pts} pts → {lastOp.name}</span>}
      </div>

      <div className="sub-list">
        {sorted.map((p, i) => {
          const color = getTeamColor(teams, p.teamId);
          const initials = getInitials(p.name);
          const isActive = selected === p.id;
          return (
            <div key={p.id} className={`sub-row${isActive ? " active" : ""}`} onClick={() => !isActive && select(p.id)}>
              <span className="sub-rank">{i + 1}</span>
              <div className="sub-avatar" style={{ background: color + "30", color }}>{initials}</div>
              <span className="sub-name">{p.name}</span>
              <span className="sub-pts">{total(p).toLocaleString()}</span>
              {!isActive && <button className="sub-btn" onClick={e => { e.stopPropagation(); select(p.id); }}>−</button>}
              {isActive && (
                <div className="sub-expand" onClick={e => e.stopPropagation()}>
                  <input
                    ref={inputRef}
                    className="sub-input"
                    type="number"
                    min="1"
                    placeholder="pts"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") confirm(); if (e.key === "Escape") { setSelected(null); setAmount(""); } }}
                    style={{ width: 90 }}
                  />
                  <button className="sub-confirm" onClick={confirm} disabled={saving}>{saving ? "…" : "✓"}</button>
                  <button className="sub-cancel" onClick={() => { setSelected(null); setAmount(""); }}>✕</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Scoreboard (pantalla para proyector) ────────────────────────────────────
const Scoreboard = ({ store }) => {
  const { teams, participants } = store;
  const sortedTeams = [...teams].sort((a, b) => b.teamPoints - a.teamPoints);
  const sortedP = [...participants].sort((a, b) => (b.personalPoints + b.bankBalance) - (a.personalPoints + a.bankBalance));
  const maxT = sortedTeams[0]?.teamPoints || 1;
  const maxP = (sortedP[0]?.personalPoints + sortedP[0]?.bankBalance) || 1;
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", padding: "40px 60px", fontFamily: "var(--font-head)" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <img src={store.config?.logoUrl || "/logo.png"} alt={store.config?.eventName || "Evento"} style={{ height: 60, filter: "brightness(0) invert(1)", marginBottom: 12 }} />
        <div style={{ color: "var(--muted)", letterSpacing: 5, fontSize: 12, textTransform: "uppercase", marginBottom: 14 }}>{store.config?.subtitle || "Sistema de puntos"} · Marcador en vivo</div>
        <a href={`/?podio&org=${new URLSearchParams(window.location.search).get("org") || ""}`} style={{ display: "inline-block", background: "#FFD70015", border: "1px solid #FFD70060", borderRadius: 8, padding: "7px 18px", color: "#FFD700", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: 1 }}>
          🏆 Ver Podio
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, maxWidth: 1400, margin: "0 auto" }}>
        <div>
          <div style={{ color: "var(--muted)", letterSpacing: 3, fontSize: 11, textTransform: "uppercase", marginBottom: 24 }}>🏆 Equipos</div>
          {sortedTeams.map((team, i) => (
            <div key={team.id} style={{ display: "flex", alignItems: "center", gap: 20, padding: "20px 26px", background: team.color + "12", border: `2px solid ${team.color}${i === 0 ? "80" : "30"}`, borderRadius: 18, marginBottom: 12 }}>
              <div style={{ fontSize: i < 3 ? 34 : 24, width: 44, textAlign: "center" }}>{medals[i] || (i + 1)}</div>
              <div style={{ fontSize: 38 }}>{team.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: team.color, marginBottom: 8 }}>{team.name}</div>
                <div style={{ background: "var(--border)", height: 7, borderRadius: 4 }}><div style={{ height: 7, borderRadius: 4, background: team.color, width: `${(team.teamPoints / maxT) * 100}%`, transition: "width 0.5s" }} /></div>
              </div>
              <div style={{ fontSize: 48, fontWeight: 800, color: team.color }}>{team.teamPoints}</div>
            </div>
          ))}
        </div>
        <div>
          <div style={{ color: "var(--muted)", letterSpacing: 3, fontSize: 11, textTransform: "uppercase", marginBottom: 24 }}>👤 Top 10 Individual</div>
          {sortedP.slice(0, 10).map((p, i) => {
            const tc = getTeamColor(teams, p.teamId); const total = p.personalPoints + p.bankBalance;
            return (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, marginBottom: 9 }}>
                <div style={{ fontSize: i < 3 ? 26 : 18, width: 36, textAlign: "center" }}>{medals[i] || (i + 1)}</div>
                <div style={{ width: 50, height: 50, borderRadius: "50%", background: tc + "30", color: tc, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 17, flexShrink: 0 }}>{getInitials(p.name)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{getTeamName(teams, p.teamId)}</div>
                  <div style={{ background: "var(--border)", height: 4, borderRadius: 2, marginTop: 6 }}><div style={{ height: 4, borderRadius: 2, background: "var(--accent)", width: `${(total / maxP) * 100}%`, transition: "width 0.5s" }} /></div>
                </div>
                <div style={{ fontSize: 34, fontWeight: 800, color: "var(--accent)" }}>{total}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Podium (pantalla de premiación con confetti) ─────────────────────────────
const Podium = ({ store }) => {
  const { teams } = store;
  const canvasRef = useRef(null);
  const [step, setStep] = useState(0);
  const sorted = [...teams].sort((a, b) => b.teamPoints - a.teamPoints).slice(0, 3);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const ctx = canvas.getContext("2d");
    const colors = ["#C9A840","#E07D3C","#E63946","#2A9D8F","#6A4C93","#1982C4","#FFD700","#ffffff","#ff69b4","#00ff88","#ff4500"];
    const pieces = Array.from({ length: 200 }, () => ({
      x: Math.random() * window.innerWidth,
      y: -Math.random() * window.innerHeight * 0.5,
      w: Math.random() * 14 + 5,
      h: Math.random() * 7 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 2.5,
      vy: Math.random() * 2.5 + 1.5,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.15,
    }));
    let alive = true;
    const draw = () => {
      if (!alive) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        if (p.y > canvas.height + 20) { p.y = -20; p.x = Math.random() * canvas.width; }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.88;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      requestAnimationFrame(draw);
    };
    draw();
    return () => { alive = false; window.removeEventListener("resize", resize); };
  }, []);

  const advance = () => setStep(s => Math.min(s + 1, 3));

  // Olympic layout: 2nd left (i=0), 1st center (i=1), 3rd right (i=2)
  const order  = [sorted[1], sorted[0], sorted[2]];
  const heights = [200, 290, 150];
  const medals  = ["🥈", "🥇", "🥉"];
  const ranks   = [2, 1, 3];
  const mColors = ["#C0C0C0", "#FFD700", "#CD7F32"];
  // Which click reveals each slot: 3rd(i=2)→click1, 2nd(i=0)→click2, 1st(i=1)→click3
  const showAt  = [2, 3, 1];

  return (
    <div onClick={advance} style={{ position: "relative", minHeight: "100vh", background: "#04080f", overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", fontFamily: "var(--font-head)", cursor: step < 3 ? "pointer" : "default", userSelect: "none" }}>
      <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 5 }} />

      {/* Header — aparece en el click 3 */}
      {step >= 3 && sorted[0] && (
        <div style={{ position: "absolute", top: "5vh", textAlign: "center", zIndex: 20, animation: "fadeInDown 0.9s ease-out both", padding: "0 24px" }}>
          <div style={{ fontSize: 13, color: "var(--accent)", letterSpacing: 6, textTransform: "uppercase", marginBottom: 14 }}>
            {store.config?.eventName || "Mi Evento"} · Premiación Final
          </div>
          <div style={{ fontSize: "clamp(38px,6.5vw,80px)", fontWeight: 900, color: "#FFD700", animation: "pulseGlow 2.5s ease-in-out infinite", lineHeight: 1.1 }}>
            {sorted[0].emoji} {sorted[0].name}
          </div>
          <div style={{ fontSize: "clamp(14px,2vw,22px)", color: "#FFD70099", marginTop: 10, letterSpacing: 3, textTransform: "uppercase" }}>
            Campeón · {sorted[0].teamPoints} puntos
          </div>
        </div>
      )}

      {/* Podium blocks */}
      <div style={{ display: "flex", alignItems: "flex-end", zIndex: 20, justifyContent: "center", gap: 6, width: "100%" }}>
        {order.map((team, i) => {
          const visible = step >= showAt[i];
          if (!visible || !team) return <div key={`ph-${i}`} style={{ width: "clamp(150px,18vw,230px)" }} />;
          return (
            <div key={team.id} style={{ width: "clamp(150px,18vw,230px)", display: "flex", flexDirection: "column", alignItems: "center", animation: "riseUp 1.1s cubic-bezier(.17,.67,.35,1) both" }}>
              <div style={{ textAlign: "center", marginBottom: 12, animation: "fadeInDown 0.8s 0.3s ease-out both" }}>
                <div style={{ fontSize: "clamp(30px,4.5vw,54px)" }}>{medals[i]}</div>
                <div style={{ fontSize: "clamp(28px,4vw,48px)" }}>{team.emoji}</div>
                <div style={{ fontSize: "clamp(13px,1.8vw,22px)", fontWeight: 800, color: mColors[i], marginTop: 6 }}>{team.name}</div>
                <div style={{ fontSize: "clamp(11px,1.4vw,17px)", color: "#888", marginTop: 3 }}>{team.teamPoints} pts</div>
              </div>
              <div style={{
                width: "100%", height: heights[i],
                background: `linear-gradient(170deg, ${team.color}45 0%, ${team.color}15 100%)`,
                border: `3px solid ${mColors[i]}`,
                borderBottom: "none",
                borderRadius: "14px 14px 0 0",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "clamp(42px,6vw,80px)", fontWeight: 900, color: mColors[i],
                textShadow: `0 0 30px ${mColors[i]}80`,
              }}>
                {ranks[i]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Base */}
      <div style={{ width: "clamp(460px,58vw,720px)", height: 20, background: "linear-gradient(90deg, #C9A84030, #C9A84070, #C9A84030)", zIndex: 20, borderRadius: "0 0 4px 4px" }} />

      {/* Hint */}
      <div style={{ position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)", fontSize: 13, color: step < 3 ? "#ffffff40" : "#ffffff15", zIndex: 20, letterSpacing: 2, textTransform: "uppercase", transition: "color 0.5s" }}>
        {step < 3 ? `Toca para continuar · ${step}/3` : ""}
      </div>
    </div>
  );
};

// ── Clientes (superadmin) ────────────────────────────────────────────────────
const Clientes = () => {
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ orgName: "", coachName: "", email: "", password: "", plan: "event", days: "7" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("organizations").select("*").order("id").then(({ data }) => { setOrgs(data || []); setLoadingOrgs(false); });
  }, []);

  const createOrg = async () => {
    if (!form.orgName.trim() || !form.email.trim() || !form.password) return;
    setSaving(true);
    const expiresAt = form.plan === "annual"
      ? new Date(Date.now() + 365 * 86400000).toISOString()
      : new Date(Date.now() + parseInt(form.days || 7) * 86400000).toISOString();
    const { data: org } = await supabase.from("organizations").insert({ name: form.orgName.trim(), owner_email: form.email.trim(), plan: form.plan, expires_at: expiresAt, active: true }).select().single();
    if (!org) { setSaving(false); return; }
    await Promise.all([
      supabase.from("coaches").insert({ name: form.coachName.trim() || form.orgName.trim(), email: form.email.trim(), password: form.password, role: "admin", organization_id: org.id }),
      supabase.from("config").insert({ organization_id: org.id, event_name: form.orgName.trim(), subtitle: "Sistema de puntos", accent_color: "#C9A840" }),
    ]);
    setOrgs(prev => [...prev, org]);
    setShowForm(false);
    setForm({ orgName: "", coachName: "", email: "", password: "", plan: "event", days: "7" });
    setSaving(false);
  };

  const toggleActive = async (org) => {
    await supabase.from("organizations").update({ active: !org.active }).eq("id", org.id);
    setOrgs(prev => prev.map(o => o.id === org.id ? { ...o, active: !o.active } : o));
  };

  const fmt = d => d ? new Date(d).toLocaleDateString("es-MX") : "Sin límite";
  const isExpired = d => d && new Date(d) < new Date();

  return (
    <div className="content">
      <div className="ph">
        <div className="ph-left"><div className="ph-title">Clientes</div><div className="ph-sub">Organizaciones activas en Pointcamp</div></div>
        <div className="ph-right"><button className="btn-add" onClick={() => setShowForm(true)}>+ Nueva organización</button></div>
      </div>
      {loadingOrgs ? <div className="spinner" style={{ marginTop: 40 }} /> : (
        <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>Organización</th><th>Plan</th><th>Expira</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {orgs.map(org => (
              <tr key={org.id}>
                <td><div className="tbl-name">{org.name}</div><div className="tbl-sub">{org.owner_email}</div></td>
                <td><span className="badge">{org.plan === "annual" ? "Anual" : "Por evento"}</span></td>
                <td style={{ color: isExpired(org.expires_at) ? "var(--danger)" : "var(--muted)", fontSize: 12 }}>{fmt(org.expires_at)}</td>
                <td><span className={`badge ${org.active && !isExpired(org.expires_at) ? "admin" : ""}`}>{!org.active ? "Inactiva" : isExpired(org.expires_at) ? "Expirada" : "Activa"}</span></td>
                <td><button className={`btn-sm ${org.active ? "danger" : "accent"}`} onClick={() => toggleActive(org)}>{org.active ? "Desactivar" : "Activar"}</button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {showForm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-hd"><div className="modal-title">Nueva Organización</div><button className="modal-close" onClick={() => setShowForm(false)}>✕</button></div>
            <div className="f-label">Nombre del evento / organización</div>
            <input className="f-input" placeholder="Ej: Campamento Verano 2026" value={form.orgName} onChange={e => setForm(f => ({ ...f, orgName: e.target.value }))} autoFocus />
            <div className="f-label">Nombre del coach principal</div>
            <input className="f-input" placeholder="Nombre completo" value={form.coachName} onChange={e => setForm(f => ({ ...f, coachName: e.target.value }))} />
            <div className="f-label">Correo de acceso</div>
            <input className="f-input" type="email" placeholder="coach@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <div className="f-label">Contraseña</div>
            <input className="f-input" placeholder="Contraseña de acceso" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <div className="f-label">Plan</div>
            <select className="f-sel" value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}><option value="event">Por evento</option><option value="annual">Anual</option></select>
            {form.plan === "event" && (<><div className="f-label">Días de acceso</div><input className="f-input" type="number" min="1" value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))} /></>)}
            <button className="btn-confirm" onClick={createOrg} style={{ opacity: saving ? 0.6 : 1 }}>{saving ? "Creando..." : "Crear organización"}</button>
            <button className="btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── App root ─────────────────────────────────────────────────────────────────
export default function LTCApp() {
  const [session, setSession] = useState(null);
  const [tab, setTab]         = useState("dashboard");
  const [modal, setModal]     = useState(null);
  const [toast, setToast]     = useState(null);
  const [showTour, setShowTour] = useState(false);

  const params      = new URLSearchParams(window.location.search);
  const isPublic    = params.has("pantalla") || params.has("podio") || params.has("subasta") || params.has("ver");
  const urlOrgId    = params.has("org") ? parseInt(params.get("org")) : (isPublic ? 1 : null);
  const effectiveId = session?.org?.id || urlOrgId;

  const store = useStore(effectiveId);
  const showToast = useCallback((msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 2400); }, []);

  const coach = session?.coach;

  const handleLogin = async (email, password) => {
    const { data: coachData } = await supabase.from("coaches").select("*").ilike("email", email).eq("password", password).single();
    if (!coachData) return "Correo o contraseña incorrectos";
    const { data: orgData } = await supabase.from("organizations").select("*").eq("id", coachData.organization_id).single();
    if (!orgData || !orgData.active) return "Esta organización no está activa";
    if (coachData.role !== "superadmin" && orgData.expires_at && new Date(orgData.expires_at) < new Date())
      return "Tu acceso ha expirado. Contacta a tu administrador para renovar.";
    setSession({ coach: toCoach(coachData), org: orgData });
    if (!localStorage.getItem(`tour_v2_${coachData.organization_id}`)) setShowTour(true);
    return null;
  };

  const handleLogout = () => { setSession(null); setTab("dashboard"); };

  const cfg   = store.config || DEFAULT_CONFIG;
  const dynCss = `:root { --accent: ${cfg.accentColor}; }`;

  useEffect(() => { document.title = cfg.eventName; }, [cfg.eventName]);

  const handleGivePersonal = (participantId, categoryId, amount, note) => {
    store.givePersonalPoints(participantId, categoryId, amount, coach.id, note);
    const p = store.participants.find(x => x.id === participantId);
    const c = store.categories.find(x => x.id === categoryId);
    showToast(`${c?.emoji || "⭐"} ${amount > 0 ? "+" : ""}${amount} pts → ${p?.name?.split(" ")[0]}`);
  };
  const handleGiveTeam = (teamId, categoryId, amount, note) => {
    store.giveTeamPoints(teamId, categoryId, amount, coach.id, note);
    const t = store.teams.find(x => x.id === teamId);
    showToast(`🏆 ${amount > 0 ? "+" : ""}${amount} pts → ${t?.name}`);
  };
  const handleDeposit = (participantId, amount) => {
    store.depositBank(participantId, amount, coach.id);
    const p = store.participants.find(x => x.id === participantId);
    showToast(`🏦 +${amount} billetes → ${p?.name?.split(" ")[0]}`);
  };

  const openPersonal = (preId) => setModal({ type: "personal", preId: preId || null });
  const openBank     = (preId) => setModal({ type: "bank",     preId: preId || null });

  if (params.has("podio"))    return (<><style>{css}</style><style>{dynCss}</style><Podium    store={store} /></>);
  if (params.has("pantalla")) return (<><style>{css}</style><style>{dynCss}</style><Scoreboard store={store} /></>);
  if (params.has("subasta"))  return (<><style>{css}</style><style>{dynCss}</style><Subasta   store={store} /></>);
  if (params.has("ver"))      return (<><style>{css}</style><style>{dynCss}</style><Ver       store={store} /></>);

  if (!coach) return (<><style>{css}</style><Login onLogin={handleLogin} /></>);

  if (store.loading) return (
    <><style>{css}</style>
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
        <img src={cfg.logoUrl || "/logo.png"} alt={cfg.eventName} style={{ width: 160, marginBottom: 24 }} />
        <div className="spinner" />
        <div style={{ color: "var(--muted)", fontSize: 13, letterSpacing: 2, textTransform: "uppercase" }}>Conectando...</div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <style>{dynCss}</style>
      <div className="app">
        <Sidebar coach={coach} tab={tab} setTab={setTab} onLogout={handleLogout} config={cfg} />
        <main className="main">
          {tab === "dashboard"     && <Dashboard store={store} coach={coach} onAction={t => { if (t === "personal") openPersonal(); else if (t === "team") setModal({ type: "team" }); else openBank(); }} />}
          {tab === "rankings"      && <Rankings store={store} />}
          {tab === "participantes" && <Participantes store={store} coach={coach} onGivePoints={id => openPersonal(id)} onBank={id => openBank(id)} />}
          {tab === "equipos"       && <Equipos store={store} coach={coach} />}
          {tab === "historial"     && <Historial store={store} coach={coach} />}
          {tab === "admin"         && <Admin store={store} coach={coach} />}
          {tab === "clientes"      && coach.role === "superadmin" && <Clientes />}
        </main>
        {modal?.type === "personal" && <GivePointsModal type="personal" participants={store.participants} teams={store.teams} categories={store.categories} coach={coach} onConfirm={handleGivePersonal} onClose={() => setModal(null)} preselectedId={modal.preId} />}
        {modal?.type === "team"     && <GivePointsModal type="team"     participants={store.participants} teams={store.teams} categories={store.categories} coach={coach} onConfirm={handleGiveTeam}    onClose={() => setModal(null)} />}
        {modal?.type === "bank"     && <BankModal participants={store.participants} teams={store.teams} onConfirm={handleDeposit} onClose={() => setModal(null)} preselectedId={modal.preId} />}
        {toast && <Toast msg={toast.msg} type={toast.type} />}
        {showTour && <Tour orgId={coach.organizationId} onDone={() => setShowTour(false)} />}
      </div>
    </>
  );
}
